import React, { useState, useEffect, useCallback } from 'react';
import ErpiaSelect from './ErpiaSelect';
import APIService from '../services/api';
import { avecCle } from '../services/cleLecture';
import { updateAoReportCache } from '../services/aoCache';
import { useTraitementsCv } from './TraitementsCv';
import DocumentViewer from './DocumentViewer';
import { estCvDocument } from '../utils/cvDocument';
import AssignMatchingModal from './AssignMatchingModal';
import { useConfirm, confirmErpia } from './ConfirmDialog';
import { useFeature } from '../services/features';
import { definirNePasContacter, confirmationLever } from '../services/nePasContacter';
import SignaturesBlock from './SignaturesBlock';
import './VueCandidats.css';
import '../ats-theme-v2.css';
// Couche « Registre » (pilote) : icônes Iconoir et Motion ne servent QUE
// quand l'essai du nouveau style est activé sur cet écran.
import {
  Search as IxSearch, UserPlus as IxUserPlus, NavArrowDown as IxDown,
  NavArrowLeft as IxLeft, NavArrowRight as IxRight, MapPin as IxPin,
  User as IxUser, RefreshDouble as IxSpin, Check as IxCheck, Trash as IxTrash,
  MinusSquare as IxMinusSq, CheckSquare as IxCheckSq, Linkedin as IxLinkedin,
} from 'iconoir-react';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { QualificationCandidat } from './Qualification';
import { ancre } from '../tutoriel/ancres';
// LE VISAGE d'un candidat : un seul composant, partagé avec la liste d'un
// besoin et le tri des profils proposés (src/components/AvatarCandidat.jsx).
import AvatarCandidat from './AvatarCandidat';
import CarteProfil from './CarteProfil';

// La palette d'avatar indexée par thème : la grammaire de CET écran.
const AV = {
  dark: [{ bg: 'rgba(255,255,255,0.07)', color: '#dfe2e8' }, { bg: 'rgba(255,255,255,0.10)', color: '#f0f2f6' }, { bg: 'rgba(255,255,255,0.05)', color: '#cfd3db' }],
  light: [{ bg: '#eceef2', color: '#2a2e38' }, { bg: '#e4e6ec', color: '#16181f' }, { bg: '#f0f1f4', color: '#3a3f4b' }],
};

const MotionDiv = motion.div;

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style, className = '' }) => <i className={`bi bi-${icon} ${className}`} style={style} />;

/* Icône à double set : Bootstrap Icons en v1, Iconoir (au trait, dessin
   d'outil de bureau) quand le Registre est actif. Même API d'appel. */
const IX = {
  search: IxSearch, 'person-plus': IxUserPlus, 'chevron-down': IxDown,
  'chevron-left': IxLeft, 'chevron-right': IxRight, 'geo-alt': IxPin,
  person: IxUser, 'arrow-repeat': IxSpin, 'check-lg': IxCheck, trash3: IxTrash,
  'dash-square': IxMinusSq, 'check2-square': IxCheckSq, linkedin: IxLinkedin,
};
const Ic = ({ v2, icon, style }) => {
  const C = v2 ? IX[icon] : null;
  if (!C) return <BI icon={icon} style={style} />;
  return <C width="1em" height="1em" strokeWidth={1.8} style={{ verticalAlign: '-0.125em', ...style }} aria-hidden="true" />;
};

/** Document bureautique : seul cas où proposer Word/PDF a un sens. */
const estDocOffice = (d) => {
  const noms = `${d?.file_name || ''} ${d?.original_name || ''} ${d?.original_filename || ''} ${d?.filename || ''} ${d?.name || ''} ${d?.url || ''}`;
  // Clairement pas bureautique : pas de menu, la conversion n'a pas de sens.
  if (/\.(pdf|jpe?g|png|gif|webp)(\?|$|\s)/i.test(noms)) return false;
  if (/\.(docx?|xlsx?|pptx?)(\?|$|\s)/i.test(noms)) return true;
  if (/word|officedocument|msword/i.test(`${d?.mime_type || d?.content_type || d?.mimetype || ''}`)) return true;
  if (d?.source === 'generation' || d?.source === 'cv_prep') return true;
  // CV téléversé dont les champs ne portent aucune extension : mieux vaut
  // proposer le choix du format que le cacher.
  return d?.est_cv === true;
};

/**
 * Bouton Télécharger. Sur un document bureautique, le survol propose le format :
 * Word (fichier natif) ou PDF (converti par le serveur, prêt à envoyer).
 * Même route dans les deux cas — seul le paramètre format change.
 */
const TelechargerDoc = ({ href, office, nom }) => {
  if (!href) return null;
  // URL brute (pas l'endpoint canonique) : pas de conversion possible.
  if (!office || !/[?&]download=1/.test(href)) {
    return <a href={href} download className="ats-edit-pen" title="Télécharger"><BI icon="download" /></a>;
  }
  // Premier choix = le format NATIF du fichier (octets tels quels — pas de
  // reconversion, donc couleurs et mise en page intactes), nommé d'après son
  // extension : une matrice .pptx propose « PowerPoint », pas « Word ».
  const natif = /\.pptx?(\?|$)/i.test(nom || '') ? { lbl: 'PowerPoint (.pptx)', ic: 'file-earmark-slides' }
    : /\.xlsx?(\?|$)/i.test(nom || '') ? { lbl: 'Excel (.xlsx)', ic: 'file-earmark-spreadsheet' }
    : { lbl: 'Word (.docx)', ic: 'file-earmark-word' };
  return (
    <span className="ats-dl-split">
      <a href={href} download className="ats-edit-pen" title="Télécharger — survolez pour choisir le format"><BI icon="download" /></a>
      <span className="ats-dl-menu">
        <a href={href} download><BI icon={natif.ic} /> {natif.lbl}</a>
        <a href={`${href}&format=pdf`} download><BI icon="file-earmark-pdf" /> PDF</a>
      </span>
    </span>
  );
};

const STEPS = ['Nouveau', 'À qualifier', 'Entretien', 'Proposition', 'Vivier'];

const STATUS = {
  dark: {
    'Nouveau': { bg: 'rgba(96,165,250,0.15)', fg: '#82b4ff' }, 'À qualifier': { bg: 'rgba(234,160,30,0.16)', fg: '#e6a93f' },
    'Entretien': { bg: 'rgba(255,255,255,0.10)', fg: '#f7f8fb' }, 'Proposition': { bg: 'rgba(45,200,110,0.15)', fg: '#56d98c' },
    'Vivier': { bg: 'rgba(148,163,184,0.13)', fg: '#aab0bd' },
  },
  light: {
    'Nouveau': { bg: '#e7f0fd', fg: '#1d63d0' }, 'À qualifier': { bg: '#fbf0dd', fg: '#9a6512' },
    'Entretien': { bg: '#e6e8ed', fg: '#14161d' }, 'Proposition': { bg: '#e6f5ec', fg: '#16794a' },
    'Vivier': { bg: '#eef0f3', fg: '#5a6270' },
  },
};
const statusColors = (s, theme) => (STATUS[theme] || STATUS.dark)[s] || (STATUS[theme] || STATUS.dark)['Vivier'];

const fullName = (c) => [c.prenom, c.nom].filter(Boolean).join(' ');

/**
 * Avatar éditable (fiche + aperçu) : survol → changer la photo, et la retirer
 * quand il y en a une. POST/DELETE /candidates/:id/photo ; les refus 400 du
 * serveur (format, poids) sont affichés tels quels ; route absente → annonce
 * sans rien modifier. Formats : JPG, PNG, WEBP, HEIC — 5 Mo max.
 */
function AvatarPhotoEditable({ cand, urlServeur, av, size, radius, fontSize, className = '', style, showNotification, onPhoto }) {
  const [local, setLocal] = useState(null);   // { url, v } après un envoi/retrait réussi
  const [busy, setBusy] = useState(false);
  const inputRef = React.useRef(null);
  useEffect(() => { setLocal(null); }, [cand?.id]);

  const url = local ? local.url : urlServeur;
  const authPhoto = { Authorization: `Bearer ${APIService.getToken()}` };

  const lireRefus = async (r) => {
    const j = await r.json().catch(() => ({}));
    if (r.ok && j.success !== false) return j;
    if ([404, 405, 501].includes(r.status) && !j.error && !j.message) {
      throw Object.assign(new Error('La photo de profil arrive côté serveur — rien n\'a été modifié.'), { absente: true });
    }
    throw new Error(j.error || j.message || `HTTP ${r.status}`);
  };

  const envoyer = async (file) => {
    if (!file || busy) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      const j = await lireRefus(await fetch(`${BASE}/candidates/${cand.id}/photo`, { method: 'POST', headers: authPhoto, body: fd }));
      const nouvelle = j?.data?.photo_preview_url || urlServeur || null;
      const v = Date.now();
      setLocal({ url: nouvelle, v });
      showNotification?.(j.message || 'Photo mise à jour', 'success');
      onPhoto?.(nouvelle, v);
    } catch (e) { showNotification?.(e.message, e.absente ? 'info' : 'error'); }
    finally { setBusy(false); }
  };

  const retirer = async () => {
    if (busy) return;
    const ok = await confirmErpia({
      title: 'Retirer la photo',
      message: `La photo de ${fullName(cand) || 'ce candidat'} sera supprimée — la fiche repasse aux initiales.`,
      confirmLabel: 'Retirer la photo',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const j = await lireRefus(await fetch(`${BASE}/candidates/${cand.id}/photo`, { method: 'DELETE', headers: authPhoto }));
      setLocal({ url: null, v: Date.now() });
      showNotification?.(j.message || 'Photo retirée', 'success');
      onPhoto?.(null, null);
    } catch (e) { showNotification?.(e.message, e.absente ? 'info' : 'error'); }
    finally { setBusy(false); }
  };

  return (
    <div className={`cand-photo ${className}`} style={{ width: size, height: size, borderRadius: radius, flex: 'none', ...style }}>
      <AvatarCandidat cand={cand} url={url} version={local?.v} av={av} size="100%" radius={radius} fontSize={fontSize} />
      <div className="cand-photo__voile" data-busy={busy ? 'true' : undefined}>
        {busy
          ? <BI icon="arrow-repeat" style={{ animation: 'ats-spin .7s linear infinite' }} />
          : <>
              <button type="button" className="cand-photo__act" title={url ? 'Changer la photo' : 'Ajouter une photo'}
                aria-label={url ? 'Changer la photo' : 'Ajouter une photo'} onClick={() => inputRef.current?.click()}>
                <BI icon="camera" />
              </button>
              {url && (
                <button type="button" className="cand-photo__act" title="Retirer la photo" aria-label="Retirer la photo" onClick={retirer}>
                  <BI icon="trash3" />
                </button>
              )}
            </>}
      </div>
      <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,.heic,image/jpeg,image/png,image/webp,image/heic" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) envoyer(f); }} />
    </div>
  );
}

/**
 * Contrat auto-signé : ni dans les documents de la fiche, ni dans l'activité.
 *
 * Ces contrats sont produits et signés par le système lui-même. Le document
 * archivé et la trace de sa signature n'apprennent rien à qui travaille sur le
 * candidat, et ils poussent hors de vue le CV et les pièces qui, eux, servent
 * tous les jours. Le contrat reste accessible depuis le dossier AO, qui est son
 * contexte : c'est là qu'on le génère et qu'on le relit.
 */
const estContratAutoSigne = (d) => {
  const type = `${d?.type_piece || d?.type || ''} ${d?.source || ''}`.toLowerCase();
  const nom = `${d?.label || ''} ${d?.name || d?.original_name || d?.file_name || d?.originalName || ''}`;
  if (/docsign|signd|auto[_ -]?sign/i.test(type)) return true;
  if (/contrat|contract/.test(type)) return true;
  return /(^|[^a-z])(cdi|cdd)[_ -]/i.test(nom) || /contrat\s+(auto[- ])?sign/i.test(nom);
};

/** Une entrée d'activité qui ne parle que de la signature automatique. */
const estActiviteAutoSignature = (texte) =>
  /auto[- ]sign|docsign|signd|envoy[ée]\s+en\s+signature|document\s+sign[ée]\s+archiv/i.test(String(texte || ''));
// Pourquoi un profil ressort d'une recherche (champ servi uniquement avec search)
const MATCH_REASON = {
  'nom/prenom': { court: 'nom', long: 'son nom' },
  titre: { court: 'via titre', long: 'son titre de poste' },
  competences: { court: 'via compétences', long: 'ses compétences' },
};
const splitSkills = (s) => (s || '').split(/[,;/•\n]+/).map(x => x.trim()).filter(Boolean);
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return '—'; } };
const fmtDateShort = (d) => { if (!d) return ''; const t = new Date(d); if (isNaN(t)) return ''; return t.toLocaleDateString('fr-CH', { day: '2-digit', month: '2-digit', year: '2-digit' }); };
const fmtDateHeure = (d) => {
  if (!d) return '';
  const t = new Date(d); if (isNaN(t)) return '';
  const j = t.toLocaleDateString('fr-CH', { day: 'numeric', month: 'long', year: 'numeric' });
  if (t.getHours() === 0 && t.getMinutes() === 0) return j;
  return `${j} à ${t.toLocaleTimeString('fr-CH', { hour: '2-digit', minute: '2-digit' })}`;
};
const candManager = (c) => c.resp_manager || c.manager || c.responsable || c.gestionnaire || c.owner || c.commercial || '';
const candMaj = (c) => c.updated_at || c.updatedAt || c.date_maj || c.maj || c.date_modification || c.modified_at || c.last_update || c.cv_updated_at || null;

// Champ éditable inline (clic pour éditer, Entrée valide, Échap annule, sauvegarde au blur)
function EditField({ value, onSave, placeholder = '—', displayStyle, inputStyle, format }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || '');
  const [saved, setSaved] = useState(false);
  useEffect(() => { if (!editing) setVal(value || ''); }, [value, editing]);
  const commit = () => {
    setEditing(false);
    const v = (val || '').trim();
    if (v !== (value || '').trim()) { onSave(v); setSaved(true); setTimeout(() => setSaved(false), 1600); }
  };
  if (editing) {
    return (
      <input className="ats-edit-input" autoFocus value={val}
        onChange={e => setVal(e.target.value)} onBlur={commit}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); commit(); } if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } }}
        style={inputStyle} />
    );
  }
  return (
    <div className="ats-editable" title="Cliquer pour modifier" style={{ cursor: 'text', display: 'inline-flex', alignItems: 'center', gap: '6px', ...displayStyle }}
      onClick={e => { e.stopPropagation(); setEditing(true); }}>
      {value ? (format ? format(value) : value) : <span style={{ color: 'var(--text-faint)' }}>{placeholder}</span>}
      {/* Affordance : rien ne disait que ces textes s'éditent au clic — un
          crayon discret apparaît au survol. */}
      <BI icon="pencil" className="ats-edit-hint" />
      {saved && <span style={{ color: '#16a34a', fontSize: '11px', fontWeight: 600 }}>✓ Enregistré</span>}
    </div>
  );
}

// Éditeur de compétences : chips en lecture, textarea (séparées par virgules) en édition
function SkillsEditor({ value, onSave, compact = false }) {
  const [editing, setEditing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [val, setVal] = useState(value || '');
  useEffect(() => { if (!editing) { setVal(value || ''); if (compact) setExpanded(false); } }, [value, editing, compact]);
  const skills = splitSkills(value);
  const visibleSkills = compact && !expanded ? skills.slice(0, 12) : skills;
  const hiddenCount = skills.length - visibleSkills.length;
  if (editing) {
    return (
      <textarea className="ats-edit-input" autoFocus value={val} rows={3}
        placeholder="Compétences séparées par des virgules…"
        onChange={e => setVal(e.target.value)}
        onClick={e => e.stopPropagation()}
        onBlur={() => { setEditing(false); if ((val || '').trim() !== (value || '').trim()) onSave(val.trim()); }}
        onKeyDown={e => { if (e.key === 'Escape') { setVal(value || ''); setEditing(false); } }} />
    );
  }
  return (
    <div className="ats-editable" title="Cliquer pour modifier" style={{ display: 'flex', flexWrap: 'wrap', gap: '7px', cursor: 'text', minHeight: '26px', alignItems: 'center' }}
      onClick={e => { e.stopPropagation(); setEditing(true); }}>
      {visibleSkills.length > 0
        ? visibleSkills.map((s, k) => <span key={k} style={{ fontSize: '12.5px', fontWeight: 500, color: 'var(--accent-soft-fg)', background: 'var(--accent-soft-bg)', padding: '5px 11px', borderRadius: '7px' }}>{s}</span>)
        : <span style={{ color: 'var(--text-faint)', fontSize: '13px' }}>+ ajouter des compétences</span>}
      {hiddenCount > 0 && <button type="button" className="ats-skills-more" onClick={e => { e.stopPropagation(); setExpanded(v => !v); }} aria-expanded={expanded}
        title={expanded ? 'Réduire les compétences' : 'Afficher toutes les compétences'}>{expanded ? 'Réduire' : `+ ${hiddenCount} compétences`}</button>}
    </div>
  );
}

// CE QU'ON NOTE SUR UN CANDIDAT — deux temps, pas dix types.
//
// Demande 14.09 : « je ne peux pas choisir si c'est une action ou un rappel,
// et ça doit être intelligent : si c'est une action, pas besoin d'échéance » ;
// « il manque un call déjà fait, ou un entretien ».
//
// Le vocabulaire (natures, temps, délais) est SERVI par le serveur
// (GET /candidates/activites/vocabulaire) : une nature ajoutée là-bas apparaît
// ici sans redéployer le front. Ce repli ne sert qu'au cas où la route n'est
// pas encore en ligne — il ne doit jamais devenir la liste de référence.
const VOCAB_DEFAUT = {
  natures: [
    { cle: 'appel', libelle: 'Appel', icone: 'telephone', passe: 'Appel passé' },
    { cle: 'entretien', libelle: 'Entretien', icone: 'people', passe: 'Entretien mené' },
    { cle: 'email', libelle: 'Email', icone: 'envelope', passe: 'Email envoyé' },
    { cle: 'message', libelle: 'Message', icone: 'chat-dots', passe: 'Message envoyé' },
    { cle: 'note', libelle: 'Note', icone: 'sticky', passe: 'Note' },
  ],
  nature_defaut: 'note',
  temps: [
    { cle: 'fait', libelle: 'Déjà fait', echeance: false },
    { cle: 'a_faire', libelle: 'À faire', echeance: true },
  ],
  delais: [
    { cle: 'aujourdhui', libelle: "Aujourd'hui" }, { cle: 'demain', libelle: 'Demain' },
    { cle: 'cette_semaine', libelle: 'Cette semaine' }, { cle: 'semaine_prochaine', libelle: 'Semaine prochaine' },
  ],
};
const CONTRAT_TYPES = ['CDI', 'CDD', 'Mission', 'Portage'];
const scoreShade = (s) => s == null ? 'var(--text-faint)' : s >= 80 ? 'var(--text-strong)' : s >= 60 ? 'var(--text)' : s >= 40 ? 'var(--text-2)' : 'var(--text-3)';
const teamInitials = (name) => name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

// Badge d'ancienneté du CV (vert < 6 mois, orange 6-12, rouge > 12)
const cvAge = (cvDate) => {
  if (!cvDate) return null;
  const days = Math.floor((Date.now() - new Date(cvDate).getTime()) / 86400000);
  if (isNaN(days)) return null;
  const months = Math.max(1, Math.round(days / 30));
  if (days < 182) return { fg: '#16a34a', bg: 'rgba(45,200,110,0.15)', label: `CV à jour · ${months} mois` };
  if (days < 365) return { fg: '#d97706', bg: 'rgba(217,119,6,0.16)', label: `CV · ${months} mois` };
  return { fg: '#cf3b32', bg: 'rgba(226,35,26,0.14)', label: `CV ancien · ${months} mois` };
};

// Surligne les @mentions (n'importe quel @token — les usernames viennent de l'annuaire réel)
// \p{L}/\p{N} et non \w : « @françois » doit rester une seule pastille (le \w
// coupait au ç → « @fran » + « çois » en texte brut).
function renderWithMentions(text) {
  if (!text) return null;
  const re = /@([\p{L}\p{N}._-]+)/gu;
  const out = []; let last = 0; let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    out.push(<span key={m.index} style={{ background: 'var(--accent-soft-bg)', color: 'var(--accent-soft-fg)', padding: '1px 5px', borderRadius: '5px', fontWeight: 600 }}>@{m[1]}</span>);
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

// ─── Fiche candidat pleine page (§7/§8) ──────────────────────────────────────
function CandidatFiche({ c, theme, onBack, onPatch, onChangeStatut, showNotification, onRefresh, onOpenBesoin, onPhotoLocal }) {
  const av = AV[theme][0];
  // « NE PAS CONTACTER » — la liste noire de contact (comme dans Boond). La
  // marque vit sur la fiche ; c'est le SERVEUR qui refuse ensuite tout envoi
  // (InMail, appel IA, invitation, convocation). Ici : la poser, la lever,
  // et la voir avant de cliquer sur « Contacter ».
  const [npcLocal, setNpcLocal] = useState(null);
  const [npcForm, setNpcForm] = useState(null);
  const [npcBusy, setNpcBusy] = useState(false);
  const npc = npcLocal ?? { actif: c.ne_pas_contacter === true, motif: c.ne_pas_contacter_motif || null };
  // L'appel vit dans services/nePasContacter — le même que la carte du besoin
  // utilise pour lever la marque. Il n'y a qu'un endroit où l'on parle au
  // serveur de cette marque, donc qu'un endroit où corriger.
  const definirNPC = async (actif, motif) => {
    setNpcBusy(true);
    const out = await definirNePasContacter(c.id, actif, motif);
    setNpcBusy(false);
    if (!out.ok) { showNotification?.(out.error, out.absente ? 'info' : 'error'); return; }
    setNpcLocal({ actif, motif: actif ? (motif || null) : null });
    setNpcForm(null);
    showNotification?.(out.message, actif ? 'info' : 'success');
    onRefresh?.();
  };
  const provType = (c.provenance_type || '').toLowerCase();
  const sourceUrl = /^https?:\/\//i.test(c.provenance_detail || '') ? c.provenance_detail : null;
  const rawSource = provType === 'linkedin' ? 'LinkedIn' : provType === 'boond' ? 'Import Boond' : (c.provenance_detail || c.provenance_type || '—');
  // Provenances techniques du sourcing — « Sourcing LinkedIn (profil LinkedIn
  // AEMAA…) — repéré sur le besoin #163 » : l'identifiant recruiter est masqué à
  // l'affichage (gardé en tooltip) et « besoin #N » devient cliquable.
  const source = typeof rawSource === 'string' ? rawSource.replace(/\s*\((?:profil\s+)?linkedin[^)]*\)/i, '').trim() : rawSource;
  const sourceTip = rawSource !== source ? rawSource : undefined;
  // Valeur « Source » : lien cliquable si l'origine porte une URL (profil LinkedIn…)
  const sourceNode = (() => {
    if (sourceUrl) return <a href={sourceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--text-strong)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>{provType === 'linkedin' && <BI icon="linkedin" style={{ color: '#0a66c2' }} />}{source} <BI icon="box-arrow-up-right" style={{ fontSize: '11px', color: 'var(--text-3)' }} /></a>;
    if (typeof source !== 'string') return source;
    const m = source.match(/^(.*?)(besoin\s*#(\d+))(.*)$/i);
    if (m && onOpenBesoin) {
      return (
        <span title={sourceTip}>
          {m[1]}
          <a onClick={() => onOpenBesoin(m[3])} style={{ color: 'var(--text-strong)', textDecoration: 'underline', cursor: 'pointer' }}>{m[2]}</a>
          {m[4]}
        </span>
      );
    }
    return <span title={sourceTip}>{source}</span>;
  })();

  // §8 — actions/rappels & activité (persistés via l'API activities)
  const [todoForm, setTodoForm] = useState(null); // { texte, nature, temps, echeance } | null
  const [vocab, setVocab] = useState(VOCAB_DEFAUT); // natures/temps/délais servis par le serveur
  const [activities, setActivities] = useState(null); // GET /activities → widget « Actions / Rappels » (manuel + Boond)
  const [timeline, setTimeline] = useState(null);      // GET /timeline → widget « Activité » (historique ERP auto)
  const [actSaving, setActSaving] = useState(false);
  const [comments, setComments] = useState(null); // GET /candidates/:id/comments
  const [draft, setDraft] = useState('');
  const [mention, setMention] = useState(null); // { query }
  const [team, setTeam] = useState([]);          // annuaire org (GET /users) pour les @mentions
  const [publishing, setPublishing] = useState(false);
  const me = React.useMemo(() => { try { return APIService.getCurrentUser(); } catch { return null; } }, []);
  const [showContract, setShowContract] = useState(false);
  const [reanalyzing, setReanalyzing] = useState(false);
  const [docs, setDocs] = useState(null);          // documents fetched from API (null = pas encore chargé)
  const [positionDocs, setPositionDocs] = useState(null); // section position_documents (groupée par besoin) du GET documents
  const [uploadingCv, setUploadingCv] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [replacingId, setReplacingId] = useState(null);
  const [replaceTargetDoc, setReplaceTargetDoc] = useState(null); // doc à remplacer (pour le file input)
  const [boondImporting, setBoondImporting] = useState(false);
  const boondPoll = React.useRef({ active: false });
  const [showPositionner, setShowPositionner] = useState(false);
  const [editingName, setEditingName] = useState(false); // édition prénom/nom dans l'en-tête
  // Onglets de la fiche : lecture rapide (Vue d'ensemble), one pager sur un besoin
  // (Carte profil), édition (Administratif).
  const [ongletFiche, setOngletFiche] = useState('apercu');
  const [nameDraft, setNameDraft] = useState({ prenom: '', nom: '' });
  const docsignOn = useFeature('docsign');   // feature flags org
  const [positions, setPositions] = useState(null); // positionnements du candidat (GET /candidats/vue/:id)
  const { confirm, confirmElement } = useConfirm();
  const [cvPicker, setCvPicker] = useState(false);  // sélection du CV source quand plusieurs
  // Le traitement CV part vers l'hôte global (TraitementsCv) : réduit en
  // pastille, il survit à un changement d'écran.
  const { lancerCv } = useTraitementsCv();
  const lancerFormatEntreprise = (url) => lancerCv({
    candidat: { ...c, cv_url: url },
    existingGenCvs: genDocs.filter(d => genLabel(d) === 'CV format entreprise').map(d => ({ id: d.id, name: genLabel(d) })),
    onUpdate: () => { loadDocs(); onRefresh?.(); [2500, 6000, 12000].forEach(ms => setTimeout(loadDocs, ms)); },
  });
  const [viewerDoc, setViewerDoc] = useState(null); // { docId | previewBase, label } pour le viewer canonique
  const [renameTarget, setRenameTarget] = useState(null); // { docId, name } → modale de renommage
  const [renameValue, setRenameValue] = useState('');
  const [renaming, setRenaming] = useState(false);
  const [renamedDocs, setRenamedDocs] = useState({}); // { [docId]: nouveauNom } — override d'affichage immédiat
  const fileInputRef = React.useRef(null);
  const docFileRef = React.useRef(null);     // « Ajouter un document »
  const [dropDocs, setDropDocs] = useState(false); // glisser-déposer sur la carte Documents
  const replaceFileRef = React.useRef(null); // « Remplacer » un document
  const draftRef = React.useRef(null);
  const cvBadge = cvAge(c.cv_updated_at);
  const authH = { Authorization: `Bearer ${APIService.getToken()}` };

  // Photo de la fiche : la liste (/candidats/vue) ne porte pas encore le champ —
  // on lit alors GET /candidates/:id, la route du contrat photo. Silencieux si
  // la route ou le champ manquent : les initiales restent.
  const [photoFiche, setPhotoFiche] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setPhotoFiche(null);
    if (c.photo_preview_url !== undefined) return undefined; // la liste porte déjà le champ
    fetch(`${BASE}/candidates/${c.id}`, { headers: authH })
      .then(r => (r.ok ? r.json() : null))
      .then(j => {
        if (cancelled || !j) return;
        const d = j.data || j.candidate || j.candidat || j;
        if (d && typeof d === 'object' && typeof d.photo_preview_url === 'string') setPhotoFiche(d.photo_preview_url);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const docText = (d) => `${d.type_piece || d.type || ''} ${d.label || d.name || ''} ${d.original_name || d.originalName || ''}`.toLowerCase();
  const docName = (d) => d.label || d.name || d.original_name || d.type_piece || d.type || 'Document';
  // La matrice de compétences est liée à un besoin précis → elle n'a rien à faire sur la fiche candidat (globale)
  // Le back expose besoin_id (NULL = pièce générique de la fiche) : c'est le
  // critère. Le repli sur « matrice » ne sert que pour les données anciennes.
  const isBesoinScoped = (d) => (d.besoin_id ?? d.besoinId) != null || /matrice/.test(docText(d));
  // Livrable généré (CV format entreprise, contrat) — jamais le CV original.
  // Critère backend : source === 'generation' ; repli texte uniquement si source absente (legacy).
  const isGenerated = (d) => !isBesoinScoped(d) && (d.source === 'generation' || /format entreprise|cv[_ ]?tat|\bcontrat\b/.test(docText(d)));
  // Pièce administrative évidente (CNI, permis, passeport, AVS…) : jamais un
  // CV, même quand l'import (Boond) l'a estampillée type_piece « cv ». Le test
  // est ancré au DÉBUT du nom pour ne pas attraper un vrai CV qui mentionne
  // « permis B » dans son titre.
  const estPieceAdmin = (d) => /^\s*(cni|carte\s+(nationale\s+)?d.{0,2}identit|pi[eè]ce\s+d.{0,2}identit|permis|passeport|avs|rib|iban|dipl[oô]me|attestation|certificat|extrait|casier|titre\s+de\s+s[ée]jour)\b/i
    .test(String(d.label || d.name || d.original_name || '').trim());
  // CV original — contrat backend : is_principal (LE critère) ou type_piece commençant par "cv".
  // On ne se base PAS sur le nom de fichier pour EXIGER « cv » (boond/upload/vivier
  // tous valides) — le nom ne sert qu'à écarter les pièces administratives.
  // is_principal reste souverain : si le serveur dit que c'est la source de
  // l'analyse, on l'affiche telle quelle plutôt que de cacher la divergence.
  const isOriginalCv = (d) => !isBesoinScoped(d) && (d.is_principal === true || (!isGenerated(d) && /^cv/i.test(d.type_piece || '') && !estPieceAdmin(d)));
  const genLabel = (d) => {
    if (d.source === 'generation' || /tat|entreprise|format/.test(docText(d))) return 'CV format entreprise';
    if (/contrat/.test(docText(d))) return 'Contrat';
    return docName(d);
  };
  // Date d'upload, quel que soit le nom de champ renvoyé par le backend
  // Marque blanche : un libellé serveur contenant encore le nom d'un client est
  // neutralisé à l'affichage (« CV TAT préparé » → « CV format entreprise préparé »).
  const deBrand = (t) => (t || '').replace(/\bCV[_ ]?TAT\b/gi, 'CV format entreprise').replace(/\bTAT\b/g, '').replace(/\s{2,}/g, ' ').trim();
  const docDate = (d) => d?.created_at || d?.createdAt || d?.uploaded_at || d?.uploadedAt || d?.upload_date || d?.date || d?.updated_at || d?.updatedAt || null;
  // « v2 · maj {date} par {qui} » quand le CONTENU a été remplacé (version > 1),
  // sinon « ajouté le {date} ». updated_at seul ne suffit pas : un re-lien
  // d'identité ne change pas le contenu et ne doit rien annoncer (contrat back).
  const docDateInfo = (d) => {
    const v = Number(d?.version ?? 1);
    const upd = d?.updated_at || d?.updatedAt;
    if (v > 1 && upd) { const t = fmtDateHeure(upd); return t ? `v${v} · maj ${t}${d.updated_by_name ? ` par ${d.updated_by_name}` : ''}` : null; }
    const t = docDate(d); const f = t ? fmtDateHeure(t) : null; return f ? `ajouté le ${f}` : null;
  };
  // Lien de téléchargement : endpoint canonique (force le download + token) si
  // docId ; sinon preview_url (route back, affichable) avant l'URL brute — qui
  // peut être un lien d'app web kDrive, inutilisable hors session.
  const docDownloadHref = (d) => {
    if (d?.id != null) return avecCle(`${BASE}/candidates/${c.id}/documents/${d.id}/preview?download=1`);
    if (d?.preview_url) return avecCle(`${d.preview_url}${d.preview_url.includes('?') ? '&' : '?'}download=1`);
    return d?.url || d?.cv_url || null;
  };
  // CV originaux : documents CV (type ou nom), sinon fallback sur c.cv_url
  const cvDocs = React.useMemo(() => {
    const fromDocs = (docs || []).filter(isOriginalCv);
    if (fromDocs.length) return fromDocs.map(d => ({ id: d.id, name: docName(d), url: d.url || d.cv_url, preview_url: d.preview_url ?? null, date: docDate(d), principal: d.is_principal === true,
      version: d.version ?? null, updated_at: d.updated_at ?? d.updatedAt ?? null, updated_by_name: d.updated_by_name ?? null }));
    if (c.cv_url) return [{ name: 'CV original', url: c.cv_url, date: c.cv_updated_at, principal: true }];
    return [];
  }, [docs, c.cv_url, c.cv_updated_at]); // eslint-disable-line react-hooks/exhaustive-deps
  // CV principal (source de l'IA & du matching) : is_principal, sinon le plus récent en repli
  const principalCv = React.useMemo(() => cvDocs.find(d => d.principal) || cvDocs[0] || null, [cvDocs]);
  const otherCvs = React.useMemo(() => cvDocs.filter(d => d !== principalCv), [cvDocs, principalCv]);
  const genDocs = React.useMemo(() => (docs || []).filter(isGenerated), [docs]); // eslint-disable-line react-hooks/exhaustive-deps
  // Pièces administratives (CNI, permis, AVS, IBAN, diplômes…) : elles vivent
  // dans l'onglet Administratif, à côté des champs qu'elles justifient.
  const adminDocs = React.useMemo(() => (docs || []).filter(d => !isGenerated(d) && !isOriginalCv(d) && !isBesoinScoped(d) && estPieceAdmin(d)), [docs]); // eslint-disable-line react-hooks/exhaustive-deps
  // Tout document renvoyé qui n'est ni CV, ni généré, ni lié à un besoin, ni
  // pièce administrative → catch-all (jamais masqué)
  const otherDocs = React.useMemo(() => (docs || []).filter(d => !isGenerated(d) && !isOriginalCv(d) && !isBesoinScoped(d) && !estPieceAdmin(d)), [docs]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDocs = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/documents`, { headers: authH });
      if (r.status === 404 || r.status === 501) { setDocs([]); return; }
      const j = await r.json();
      const arr = j.documents || j.data?.documents || j.candidate_documents
        || (Array.isArray(j.data) ? j.data : null) || (Array.isArray(j) ? j : []);
      // Les CV préparés POUR UN BESOIN (source cv_prep, un par couple
      // candidat×besoin) s'affichent dans les Positionnements, pas ici. Mais un
      // CV généré depuis la fiche sans besoin n'appartient à aucun
      // positionnement : l'exclure le rendait introuvable.
      setDocs((Array.isArray(arr) ? arr : [])
        .filter(d => d.source !== 'cv_prep' || (d.besoin_id == null && d.besoinId == null && d.position_id == null))
        .filter(d => !estContratAutoSigne(d)));
      setPositionDocs(j.position_documents || j.data?.position_documents || null);
    } catch (e) { setDocs([]); }
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadDocs(); }, [loadDocs]);

  // ── Discussion interne : annuaire (@mentions) + commentaires ──
  useEffect(() => {
    // Internes uniquement : les comptes ressource ne sont pas mentionnables
    fetch(`${BASE}/users`, { headers: authH })
      .then(r => r.ok ? r.json() : {})
      .then(j => setTeam((j.data || j.users || (Array.isArray(j) ? j : [])).filter(u => u.username && !(u.role === 'ressource' || u.ressource === true))))
      .catch(() => setTeam([]));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadComments = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/comments`, { headers: authH });
      const j = await r.json().catch(() => ({}));
      setComments(j.data || j.comments || (Array.isArray(j) ? j : []));
    } catch { setComments([]); }
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadComments(); }, [loadComments]);

  // ── Feed d'activité (actions/rappels manuels + historique importé de Boond) ──
  const actText = (a) => a.label || a.description || a.text || a.message || a.action || a.note || a.event || a.title || '—';
  const actAuthor = (a) => a.author || a.username || a.created_by || a.author_name || null;
  const actDate = (a) => a.date || a.created_at || a.createdAt || a.due || a.reminder_at || a.timestamp || null;
  const actSource = (a) => (a.source || '').toLowerCase();
  const actIsBoond = (a) => /boond|import/.test(actSource(a));
  const actIsManual = (a) => actSource(a) === 'manual' || (!actSource(a) && !!actAuthor(a));
  const loadActivities = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/activities`, { headers: authH });
      if (r.status === 404 || r.status === 501) { setActivities([]); return; }
      const j = await r.json();
      const arr = j.activities || j.data?.activities || j.candidate_activities
        || (Array.isArray(j.data) ? j.data : null) || (Array.isArray(j) ? j : []);
      setActivities(Array.isArray(arr) ? arr : []);
    } catch (e) { setActivities([]); }
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadActivities(); }, [loadActivities]);

  // ── Timeline ERP (historique auto : positionnements, changements de statut…) → widget « Activité » ──
  const loadTimeline = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/timeline`, { headers: authH });
      if (r.status === 404 || r.status === 501) { setTimeline([]); return; }
      const j = await r.json();
      const arr = j.timeline || j.data?.timeline || j.events || j.candidate_timeline
        || (Array.isArray(j.data) ? j.data : null) || (Array.isArray(j) ? j : []);
      setTimeline(Array.isArray(arr) ? arr : []);
    } catch (e) { setTimeline([]); }
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadTimeline(); }, [loadTimeline]);

  // Positionnements : la fiche reçoit un candidat « liste » sans ses positionnements.
  // On va les chercher séparément (GET /candidats/vue/:id → champ positionnements),
  // avec repli sur GET /candidates/:id/besoins.
  // QUI A CRÉÉ LA FICHE : la ligne de liste ne le porte pas, la vue détaillée
  // si. On le retient au passage plutôt que d'ajouter un aller-retour.
  const [creePar, setCreePar] = useState(null);
  const loadPositions = useCallback(async () => {
    try {
      const r = await fetch(`${BASE}/candidats/vue/${c.id}`, { headers: authH });
      if (r.ok) {
        const j = await r.json();
        const cand = j.data || j.candidat || j;
        if (cand?.created_by_name) setCreePar(cand.created_by_name);
        const arr = cand?.positionnements || cand?.besoins || j.positionnements;
        if (Array.isArray(arr)) { setPositions(arr); return arr; }
      }
    } catch (e) { /* repli ci-dessous */ }
    try {
      const r2 = await fetch(`${BASE}/candidates/${c.id}/besoins`, { headers: authH });
      if (r2.ok) {
        const j2 = await r2.json();
        const arr2 = j2.besoins || j2.positionnements || j2.data || (Array.isArray(j2) ? j2 : []);
        const list = Array.isArray(arr2) ? arr2 : [];
        setPositions(list);
        return list;
      }
    } catch (e) { /* ignore */ }
    setPositions([]);
    return [];
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadPositions(); }, [loadPositions]);

  // CV spécifique par besoin : associer un document existant du candidat à un
  // positionnement (le matching AO lira ce CV-là), ou revenir au CV générique.
  const [cvPickerBesoin, setCvPickerBesoin] = useState(null); // { besoinId, titre } | null
  // Le sélecteur ne montre que des CV ; « Afficher tous les documents » lève le
  // filtre pour les fiches dont les CV sont mal étiquetés.
  const [cvPickerTous, setCvPickerTous] = useState(false);
  const [cvBesoinBusy, setCvBesoinBusy] = useState(false);
  const [useForBesoin, setUseForBesoin] = useState(null);  // document à rattacher à un besoin
  // Pièces liées à un besoin (CV préparé, matrice…) : suppression depuis la fiche.
  const [delPosDoc, setDelPosDoc] = useState(null);        // id en cours de suppression
  const deletePositionDoc = async (d, besoinId) => {
    const pdId = d.id ?? d.position_document_id ?? null;
    const label = deBrand(d.label || d.name || d.original_name || 'ce document');
    if (pdId == null) return;
    if (!(await confirm({
      title: 'Supprimer la pièce du besoin',
      message: `Supprimer « ${label} » de ce besoin ?`,
      detail: 'Le document est retiré du positionnement. La fiche candidat n\'est pas touchée.',
      confirmLabel: 'Supprimer',
    }))) return;
    setDelPosDoc(pdId);
    try {
      // Route plate : candidate_id / besoin_id en query, pour obtenir le 404
      // « hors de ce positionnement » plutôt qu'une suppression à l'aveugle.
      const qs = new URLSearchParams({ candidate_id: String(c.id), ...(besoinId != null ? { besoin_id: String(besoinId) } : {}) });
      let r = await fetch(`${BASE}/position-documents/${pdId}?${qs.toString()}`, { method: 'DELETE', headers: authH });
      if (r.status === 405) {
        r = await fetch(`${BASE}/besoins/${besoinId}/candidates/${c.id}/documents/${pdId}`, { method: 'DELETE', headers: authH });
      }
      if (r.status === 501) {
        showNotification?.('La suppression d\'une pièce du besoin arrive côté serveur.', 'error');
        return;
      }
      const j = await r.json().catch(() => ({}));
      // 409 : un CV est en cours de préparation sur ce positionnement.
      if (r.status === 409) {
        showNotification?.(j.message || j.error || 'Une préparation de CV est en cours sur ce besoin — réessayez quand elle sera terminée.', 'error');
        return;
      }
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      // Les booléens disent ce qui a été épargné : on l'affiche au lieu de le
      // promettre dans un texte figé.
      const gardes = [
        j.kdrive_conserve ? 'fichier kDrive conservé' : null,
        j.candidate_document_conserve ? 'fiche candidat inchangée' : null,
      ].filter(Boolean);
      showNotification?.(`« ${j.label || label} » retiré du besoin${gardes.length ? ` — ${gardes.join(', ')}` : ''}`, 'success');
      // report présent = la pièce appartenait aussi au dossier AO : les deux
      // vues se rafraîchissent avec, sans re-scan.
      if (j.report && j.ao_id != null) updateAoReportCache(j.ao_id, j.report);
      loadPositions(); loadDocs();
    } catch (e) { showNotification?.(`Échec : ${e.message}`, 'error'); }
    finally { setDelPosDoc(null); }
  };
  const setBesoinCv = async (besoinId, docId) => {
    setCvBesoinBusy(true);
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/besoins/${besoinId}/cv`, {
        method: 'PUT', headers: { ...authH, 'Content-Type': 'application/json' },
        // Les deux graphies : le reste de l'API s'est standardisé sur document_id.
        body: JSON.stringify({ doc_id: docId, document_id: docId }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || j.message || `HTTP ${r.status}`);
      setCvPickerBesoin(null); setUseForBesoin(null);
      // La réponse porte la désignation et le document retenu : plus besoin de
      // relire les positionnements pour vérifier. L'identifiant renvoyé est
      // l'identifiant résolu, il peut différer de celui envoyé.
      const label = j.label || j.cv_label || null;
      setPositions(prev => (prev || []).map(p => (
        String(p.besoin_id ?? p.id ?? p.besoinId) === String(besoinId)
          ? { ...p, cv_designe: j.cv_designe === true, cv_label: label,
              has_cv_besoin: j.cv_designe === true, cv_document_id: j.document_id ?? j.doc_id ?? docId }
          : p
      )));
      showNotification?.(label ? `CV du besoin défini : ${label}` : 'CV du besoin défini', 'success');
    } catch (e) { showNotification?.(`Échec : ${e.message}`, 'error'); } finally { setCvBesoinBusy(false); }
  };
  const removeBesoinCv = async (besoinId) => {
    setCvBesoinBusy(true);
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/besoins/${besoinId}/cv`, { method: 'DELETE', headers: authH });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      showNotification?.('Retour au CV de la fiche', 'success');
      setCvPickerBesoin(null);
      setPositions(prev => (prev || []).map(p => (
        String(p.besoin_id ?? p.id ?? p.besoinId) === String(besoinId)
          ? { ...p, cv_designe: false, cv_label: null, has_cv_besoin: false, cv_document_id: null }
          : p
      )));
    } catch (e) { showNotification?.(`Échec : ${e.message}`, 'error'); } finally { setCvBesoinBusy(false); }
  };

  const removeActivity = async (id) => {
    if (id == null) return;
    if (!(await confirm({ title: 'Supprimer l\'action', message: 'Supprimer cette action / ce rappel ?', confirmLabel: 'Supprimer' }))) return;
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/activities/${id}`, { method: 'DELETE', headers: authH });
      if (r.status === 403) { showNotification?.('Entrée importée de Boond — suppression impossible', 'error'); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      showNotification?.('Action supprimée', 'success');
      await loadActivities();
    } catch (e) { showNotification?.('Erreur suppression : ' + e.message, 'error'); }
  };

  const reanalyzeCV = async () => {
    setReanalyzing(true);
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/extract-cv`, { method: 'POST', headers: authH });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      showNotification?.('Ré-analyse du CV lancée — résumé & compétences mis à jour sous peu', 'success');
    } catch (e) { showNotification?.('Erreur ré-analyse : ' + e.message, 'error'); }
    finally { setReanalyzing(false); }
  };

  const uploadCv = async (file) => {
    if (!file) return;
    setUploadingCv(true);
    try {
      await postDoc(file, 'cv');
      showNotification?.('CV uploadé', 'success');
      await loadDocs(); onRefresh?.();
    } catch (e) { showNotification?.('Erreur upload CV : ' + e.message, 'error'); }
    finally { setUploadingCv(false); }
  };

  // Upload générique d'un document (POST FormData) — renvoie la réponse ; type_piece libre
  const postDoc = async (file, typePiece, label) => {
    const fd = new FormData();
    fd.append('file', file);
    if (typePiece) { fd.append('type', typePiece); fd.append('type_piece', typePiece); }
    if (label) fd.append('label', label);
    const r = await fetch(`${BASE}/candidates/${c.id}/documents`, { method: 'POST', headers: authH, body: fd });
    if (!r.ok) {
      // Le motif du refus est dans la réponse : le montrer, pas le code HTTP.
      const j = await r.json().catch(() => ({}));
      throw new Error(j.error || j.message || `HTTP ${r.status}`);
    }
    return r.json().catch(() => ({}));
  };

  // Ajouter un ou PLUSIEURS documents sur la fiche : la route d'upload est
  // unitaire, on la boucle — chaque fichier a son propre verdict, et le bilan
  // final dit exactement ce qui est passé et ce qui a échoué.
  const uploadDocs = async (fichiers) => {
    const liste = Array.from(fichiers || []).filter(Boolean);
    if (!liste.length) return;
    setUploadingDoc(true);
    const echecs = [];
    for (const file of liste) {
      try { await postDoc(file, 'document'); }
      catch (e) { echecs.push(`${file.name} (${e.message})`); }
    }
    const ok = liste.length - echecs.length;
    if (!echecs.length) showNotification?.(ok > 1 ? `${ok} documents ajoutés` : 'Document ajouté', 'success');
    else showNotification?.(`${ok} ajouté(s), ${echecs.length} en échec : ${echecs.join(', ')}`, 'error');
    await loadDocs(); onRefresh?.();
    setUploadingDoc(false);
  };

  // Définir un document comme CV principal (source IA & matching) — le backend désactive l'ancien principal
  const setPrincipalCv = async (docId) => {
    if (docId == null) return;
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/documents/${docId}`, {
        method: 'PATCH', headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_principal: true })
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      showNotification?.('CV principal mis à jour', 'success');
      await loadDocs(); onRefresh?.();
    } catch (e) { showNotification?.('Erreur : ' + e.message, 'error'); }
  };

  // Remplacer un document : upload du nouveau fichier (même type) puis suppression de l'ancien
  const replaceDoc = async (file, oldDoc) => {
    if (!file || !oldDoc) return;
    setReplacingId(oldDoc.id);
    try {
      await postDoc(file, oldDoc.type_piece || oldDoc.type || 'document', oldDoc.label || oldDoc.name);
      if (oldDoc.id != null) {
        await fetch(`${BASE}/candidates/${c.id}/documents/${oldDoc.id}`, { method: 'DELETE', headers: authH }).catch(() => {});
      }
      showNotification?.('Document remplacé', 'success');
      await loadDocs(); onRefresh?.();
    } catch (e) { showNotification?.('Erreur remplacement : ' + e.message, 'error'); }
    finally { setReplacingId(null); }
  };

  // Import des documents Boond pour ce candidat (asynchrone + poll)
  const pollBoondImport = async () => {
    try {
      const r = await fetch(`${BASE}/candidats/boond/import-documents/status`, { headers: authH });
      const d = await r.json();
      if (d.running && boondPoll.current.active) { setTimeout(pollBoondImport, 2000); return; }
      boondPoll.current.active = false;
      setBoondImporting(false);
      showNotification?.(`Import Boond terminé : ${d.resumes_ok ?? 0} CV · ${d.files_ok ?? 0} pièce(s) · ${d.failed ?? 0} échec(s) · ${d.skipped ?? 0} ignoré(s)`, 'success');
      await loadDocs(); onRefresh?.();
    } catch (e) { boondPoll.current.active = false; setBoondImporting(false); showNotification?.('Erreur suivi import Boond', 'error'); }
  };
  const importFromBoond = async () => {
    setBoondImporting(true);
    try {
      const r = await fetch(`${BASE}/candidats/boond/import-documents`, { method: 'POST', headers: { ...authH, 'Content-Type': 'application/json' }, body: JSON.stringify({ candidateId: c.id }) });
      const d = await r.json();
      if (!r.ok || d.success === false) throw new Error(d.error || `HTTP ${r.status}`);
      boondPoll.current.active = true;
      pollBoondImport();
    } catch (e) { setBoondImporting(false); showNotification?.('Erreur import Boond : ' + e.message, 'error'); }
  };
  useEffect(() => () => { boondPoll.current.active = false; }, []);

  const deleteDoc = async (docId, label) => {
    if (docId == null) return;
    if (!(await confirm({ title: 'Supprimer le document', message: `Supprimer « ${label || 'ce document'} » ?`, detail: 'Cette action est irréversible.', confirmLabel: 'Supprimer' }))) return;
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/documents/${docId}`, { method: 'DELETE', headers: authH });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      showNotification?.('Document supprimé', 'success');
      await loadDocs(); onRefresh?.();
    } catch (e) { showNotification?.('Erreur suppression : ' + e.message, 'error'); }
  };

  const renameDoc = (docId, currentName) => {
    if (docId == null) return;
    setRenameTarget({ docId, name: currentName || '' });
    setRenameValue(currentName || '');
  };
  const doRename = async () => {
    if (!renameTarget) return;
    const { docId } = renameTarget;
    const name = renameValue.trim();
    if (!name) return;
    if (name === (renameTarget.name || '')) { setRenameTarget(null); return; }
    setRenaming(true);
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/documents/${docId}`, {
        method: 'PATCH', headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name })
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Override d'affichage immédiat + maj optimiste de la liste (le nom s'actualise tout de suite sur la fiche)
      setRenamedDocs(m => ({ ...m, [docId]: name }));
      setDocs(prev => Array.isArray(prev) ? prev.map(d => d.id === docId ? { ...d, name } : d) : prev);
      showNotification?.('Document renommé', 'success');
      setRenameTarget(null);
      await loadDocs(); onRefresh?.();
    } catch (e) { showNotification?.('Erreur renommage : ' + e.message, 'error'); }
    finally { setRenaming(false); }
  };
  // Nom d'affichage : l'override de renommage prime sur le libellé calculé
  const dispName = (d, fallback) => (d?.id != null && renamedDocs[d.id]) || fallback;

  // Générer le CV au format entreprise : exige un CV original ; choix si plusieurs
  const generateTatCv = () => {
    if (cvDocs.length === 0) { showNotification?.('Ajoute d\'abord un CV original avant de générer le format entreprise.', 'error'); return; }
    if (cvDocs.length === 1) { lancerFormatEntreprise(cvDocs[0].url); return; }
    setCvPicker(true);
  };

  // Le vocabulaire vient du serveur ; son absence (route pas encore en ligne)
  // n'empêche rien — on garde le repli, sans le dire : il n'y a rien à faire
  // pour l'utilisateur.
  useEffect(() => {
    let annule = false;
    (async () => {
      try {
        const r = await fetch(`${BASE}/candidates/activites/vocabulaire`, { headers: authH });
        if (!r.ok) return;
        const j = await r.json();
        const v = j.data || j;
        if (!annule && Array.isArray(v?.natures) && v.natures.length) setVocab(v);
      } catch (e) { /* repli en dur */ }
    })();
    return () => { annule = true; };
  }, [c.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const tempsDe = (cle) => (vocab.temps || []).find(x => x.cle === cle) || { cle, libelle: cle, echeance: cle === 'a_faire' };
  const natureDe = (cle) => (vocab.natures || []).find(n => n.cle === cle) || null;
  const openTodo = () => setTodoForm({ texte: '', nature: vocab.nature_defaut || 'note', temps: 'fait', echeance: (vocab.delais?.[0]?.cle) || 'aujourdhui' });

  // L'auteur n'est PAS choisi : le backend trace l'utilisateur connecté à partir du token.
  // L'ÉCHÉANCE NE PART QUE POUR UN RAPPEL : le serveur refuse une action déjà
  // faite accompagnée d'une date due, et il a raison — les envoyer toutes les
  // deux « au cas où » ferait échouer une saisie correcte.
  const saveTodo = async () => {
    const texte = (todoForm?.texte || '').trim();
    if (!texte) return;
    const aFaire = tempsDe(todoForm.temps).echeance === true;
    setActSaving(true);
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/activities`, {
        method: 'POST', headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ texte, nature: todoForm.nature, temps: todoForm.temps,
          ...(aFaire ? { echeance: todoForm.echeance } : {}) }),
      });
      const j = await r.json().catch(() => ({}));
      // Le message du serveur, mot pour mot : c'est lui qui dit ce qui se
      // corrige (« un rappel a besoin d'une échéance »).
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.(aFaire ? 'Rappel posé' : 'Noté sur la fiche', 'success');
      setTodoForm(null);
      await loadActivities();
    } catch (e) { showNotification?.(e.message, 'error'); }
    finally { setActSaving(false); }
  };

  // TRAITER un rappel : il passe à « fait », il ne disparaît pas. L'historique
  // d'un candidat est ce qui permet de dire « on l'a déjà relancé deux fois ».
  const traiterActivity = async (id) => {
    if (id == null) return;
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/activities/${id}`, {
        method: 'PATCH', headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ fait: true }),
      });
      if (r.status === 404 || r.status === 501) { showNotification?.('Le passage à « fait » arrive côté serveur — rien n\'a changé.', 'info'); return; }
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      showNotification?.('Marqué comme fait', 'success');
      await loadActivities();
    } catch (e) { showNotification?.(e.message, 'error'); }
  };

  // \p{L}/\p{N} partout (détection, insertion, résolution) : les usernames
  // accentués (françois…) ne sont plus coupés au premier caractère non-ASCII.
  const onDraftChange = (e) => {
    const v = e.target.value; setDraft(v);
    const before = v.slice(0, e.target.selectionStart);
    const mm = before.match(/@([\p{L}\p{N}._-]*)$/u);
    setMention(mm ? { query: mm[1].toLowerCase() } : null);
  };
  const insertMention = (member) => {
    const el = draftRef.current; const pos = el ? el.selectionStart : draft.length;
    const before = draft.slice(0, pos).replace(/@([\p{L}\p{N}._-]*)$/u, `@${member.username} `);
    setDraft(before + draft.slice(pos)); setMention(null); el && el.focus();
  };
  const escRe = (s) => String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const publish = async () => {
    const body = draft.trim();
    if (!body || publishing) return;
    // Résout les @username tapés en userIds (le backend re-détecte aussi en secours).
    const mentioned = team.filter(u => new RegExp(`@${escRe(u.username)}(?![\\p{L}\\p{N}._-])`, 'iu').test(body));
    setPublishing(true);
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/comments`, {
        method: 'POST', headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ body, mentions: mentioned.map(u => u.id) }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json().catch(() => ({}));
      // Notification in-app des mentionnés (cloche) — même mécanique que les notes
      // besoin. Seulement si le backend ne l'a pas déjà fait (j.notified présent).
      const toNotify = mentioned.filter(u => u.id !== me?.id);
      if (j.notified == null && toNotify.length) {
        await Promise.all(toNotify.map(u =>
          fetch(`${BASE}/notifications/inapp`, {
            method: 'POST', headers: { ...authH, 'Content-Type': 'application/json' },
            body: JSON.stringify({
              user_id: u.id,
              message: `📌 ${me?.username || 'Un collègue'} vous a mentionné sur la fiche de ${fullName(c) || 'un candidat'}`,
              type: 'mention',
              ref_type: 'candidat',
              ref_id: c.id,
            }),
          }).catch(() => {})
        ));
      }
      const notified = j.notified ?? toNotify.length;
      if (notified) showNotification?.(`${notified} personne(s) notifiée(s)`, 'success');
      setDraft(''); setMention(null);
      await loadComments();
    } catch (e) { showNotification?.('Échec de la publication : ' + e.message, 'error'); }
    finally { setPublishing(false); }
  };
  const deleteComment = async (cm) => {
    const id = cm.id ?? cm.comment_id;
    if (id == null) return;
    if (!await confirmErpia({ message: 'Supprimer ce commentaire ?' })) return;
    try {
      const r = await fetch(`${BASE}/candidates/${c.id}/comments/${id}`, { method: 'DELETE', headers: authH });
      if (r.status === 403) { showNotification?.('Seul l\'auteur ou un admin peut supprimer ce commentaire.', 'error'); return; }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await loadComments();
    } catch (e) { showNotification?.('Échec de la suppression : ' + e.message, 'error'); }
  };

  const ld = { color: 'var(--text-3)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' };
  const vl = { marginTop: '4px', fontSize: '13.5px', fontWeight: 500, color: 'var(--text)' };
  const sectTitle = { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '12px' };
  const cardBox = { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '18px 20px' };
  const filtered = mention ? team.filter(u => u.username.toLowerCase().includes(mention.query) || (u.email || '').toLowerCase().includes(mention.query)).slice(0, 6) : [];

  // Ligne document générique (aperçu, renommer, remplacer, télécharger,
  // supprimer) — partagée entre « Autres documents » et les pièces
  // administratives de l'onglet Administratif.
  const ligneDocSimple = (d, i, icone = 'file-earmark') => (
    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 0' }}>
      <BI icon={icone} style={{ color: 'var(--text-3)', flex: 'none' }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '12.5px', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dispName(d, docName(d))}</div>
        {docDateInfo(d) && <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{docDateInfo(d)}</div>}
      </div>
      {d.id != null && <button className="ats-edit-pen" title="Aperçu" onClick={() => setViewerDoc({ docId: d.id, label: dispName(d, docName(d)) })}><BI icon="eye" /></button>}
      {d.id != null && <button className="ats-edit-pen" title="Renommer" onClick={() => renameDoc(d.id, dispName(d, docName(d)))}><BI icon="pencil" /></button>}
      {d.id != null && <button className="ats-edit-pen" title="Remplacer par un nouveau fichier" disabled={replacingId === d.id} onClick={() => { setReplaceTargetDoc(d); replaceFileRef.current?.click(); }}><BI icon={replacingId === d.id ? 'arrow-repeat' : 'arrow-clockwise'} style={replacingId === d.id ? { animation: 'ats-spin .7s linear infinite' } : undefined} /></button>}
      <TelechargerDoc href={docDownloadHref(d)} office={estDocOffice(d)} nom={d.original_name || d.file_name || d.name || d.label || ''} />
      {d.id != null && <button className="ats-edit-pen" title="Supprimer" onClick={() => deleteDoc(d.id, docName(d))} style={{ color: 'var(--danger-fg)' }}><BI icon="trash3" /></button>}
    </div>
  );

  // Bloc Administratif : les champs du contrat ET les pièces qui les
  // justifient (identité, permis, AVS, IBAN, diplômes…) — la lecture rapide
  // d'un côté, l'édition de l'autre.
  const blocAdministratif = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={cardBox}>
            <div style={{ ...sectTitle, display: 'flex', alignItems: 'center', gap: '7px' }}>Administratif <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0, color: 'var(--text-faint)', fontSize: '10.5px' }}>· clic pour éditer</span></div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px', marginBottom: '14px' }}>
              {/* Données du contrat : sans elles, chaque génération les redemande.
                  Saisies une fois ici, le serveur les reprend pour tous les
                  contrats du candidat. */}
              <div><div style={ld}>Civilité</div><EditField value={c.civilite} placeholder="—" displayStyle={vl} onSave={v => onPatch(c.id, { civilite: v }, { civilite: v })} /></div>
              <div><div style={ld}>Date de naissance</div><EditField value={c.date_naissance} placeholder="—" displayStyle={vl} onSave={v => onPatch(c.id, { date_naissance: v }, { date_naissance: v })} /></div>
              <div style={{ gridColumn: '1 / -1' }}><div style={ld}>Adresse</div><EditField value={c.adresse} placeholder="—" displayStyle={vl} onSave={v => onPatch(c.id, { adresse: v }, { adresse: v })} /></div>
              <div><div style={ld}>N° AVS</div><EditField value={c.avs} placeholder="—" displayStyle={vl} onSave={v => onPatch(c.id, { avs: v }, { avs: v })} /></div>
              <div><div style={ld}>Permis de travail</div><EditField value={c.permis} placeholder="—" displayStyle={vl} onSave={v => onPatch(c.id, { permis: v }, { permis: v })} /></div>
              <div><div style={ld}>Nationalité</div><EditField value={c.nationalite} placeholder="—" displayStyle={vl} onSave={v => onPatch(c.id, { nationalite: v }, { nationalite: v })} /></div>
              <div><div style={ld}>Disponibilité</div><EditField value={c.disponibilite} placeholder="—" displayStyle={vl} onSave={v => onPatch(c.id, { disponibilite: v }, { disponibilite: v })} /></div>
              <div><div style={ld}>Salaire actuel</div><EditField value={c.salaire_actuel} placeholder="N/C" displayStyle={vl} onSave={v => onPatch(c.id, { salaire_actuel: v }, { salaire_actuel: v })} /></div>
              <div><div style={ld}>Salaire souhaité</div><EditField value={c.salaire_souhaite} placeholder="—" displayStyle={vl} onSave={v => onPatch(c.id, { salaire_souhaite: v }, { salaire_souhaite: v })} /></div>
              <div style={{ gridColumn: '1 / -1' }}><div style={ld}>Contrat</div><EditField value={c.contrat} placeholder="Pas de contrat actuellement" displayStyle={vl} onSave={v => onPatch(c.id, { contrat: v }, { contrat: v })} /></div>
            </div>
            <button className="ats-btn ats-btn-primary" onClick={() => setShowContract(true)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '11px', borderRadius: '9px', fontSize: '13.5px', fontWeight: 600 }}><BI icon="file-earmark-text" /> Générer le contrat</button>
          </div>

          {/* Pièces administratives — mêmes routes et mêmes gestes que la
              carte Documents ; seul le rangement change. */}
          <div style={cardBox}>
            <div style={{ ...sectTitle, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '10px' }}>
              <span style={{ flex: 1 }}>Documents administratifs</span>
              <button className="ats-btn ats-btn-ghost" onClick={() => docFileRef.current?.click()} disabled={uploadingDoc}
                style={{ padding: '6px 11px', borderRadius: '7px', fontSize: '12px', fontWeight: 600, textTransform: 'none', letterSpacing: 0, opacity: uploadingDoc ? 0.6 : 1 }}>
                <BI icon={uploadingDoc ? 'arrow-repeat' : 'file-earmark-plus'} /> {uploadingDoc ? 'Ajout…' : 'Ajouter'}
              </button>
            </div>
            {adminDocs.length === 0
              ? <div style={{ padding: '12px 14px', border: '1px dashed var(--border)', borderRadius: '9px', color: 'var(--text-3)', fontSize: '12.5px' }}>
                  Aucune pièce administrative — pièce d'identité, permis, AVS, IBAN, diplômes, attestations… Le classement se fait d'après le nom du fichier.
                </div>
              : adminDocs.map((d, i) => ligneDocSimple(d, i, 'file-earmark-lock'))}
          </div>
    </div>
  );

  return (
    <div>
      {/* Fil d'ariane (02.11 : plus de bouton « Candidats » — le retour du
          navigateur ramène à la liste ; « Candidats » reste cliquable pour une
          fiche ouverte depuis un lien, sans page précédente). */}
      <div className="ats-mono" style={{ marginBottom: '14px', fontSize: '11px', color: 'var(--text-3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        <button type="button" className="vc-ariane" onClick={onBack}>Candidats</button> / <span style={{ color: 'var(--text-2)' }}>{fullName(c) || 'Candidat'}</span>
      </div>

      {/* QUALIFICATION — l'arrivée depuis la notification. Ne s'affiche que
          pour la personne à qui la demande est adressée, et en TÊTE de fiche :
          c'est ce qu'elle est venue faire. Personne d'autre ne le voit. */}
      <QualificationCandidat candidateId={c.id} showNotification={showNotification} onChange={onRefresh} />

      {/* Hero identité — avatar, dossier, score et actions */}
      <div {...ancre('fiche-entete')} style={{ ...cardBox, display: 'flex', gap: '16px', alignItems: 'flex-start', padding: '22px 24px', flexWrap: 'wrap' }}>
        <AvatarPhotoEditable cand={c} urlServeur={c.photo_preview_url !== undefined ? c.photo_preview_url : photoFiche} av={av}
          size="72px" radius="18px" fontSize="23px" style={{ boxShadow: '0 0 0 5px var(--bg)' }}
          showNotification={showNotification} onPhoto={(u, v) => onPhotoLocal?.(c.id, u, v)} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {(() => {
            const majTs = c.updated_at || c.cv_updated_at;
            const maj = majTs && !Number.isNaN(Date.parse(majTs)) ? fmtDate(majTs) : null;
            return <div className="ats-mono" style={{ fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '5px' }}>Profil candidat{maj ? ` · mis à jour le ${maj}` : ''}</div>;
          })()}
          <div style={{ display: 'flex', alignItems: 'center', gap: '9px', flexWrap: 'wrap' }}>
            {!editingName ? (
              <>
                <h1 className="ats-h1-sub">{fullName(c) ? <>{fullName(c)}<span style={{ color: 'var(--accent-soft-fg, var(--brand))' }}>.</span></> : <span style={{ color: 'var(--text-faint)', fontStyle: 'italic' }}>Sans nom</span>}</h1>
                <button className="ats-edit-pen" title="Renommer le candidat" onClick={() => { setNameDraft({ prenom: c.prenom || '', nom: c.nom || '' }); setEditingName(true); }}><BI icon="pencil" /></button>
              </>
            ) : (() => {
              const inp = { width: '150px', padding: '7px 10px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--surface)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '14px', fontWeight: 600, outline: 'none' };
              const save = () => { const p = nameDraft.prenom.trim(), n = nameDraft.nom.trim(); if (!p && !n) return; onPatch(c.id, { prenom: p, nom: n }, { prenom: p, nom: n }); setEditingName(false); };
              const keys = (e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditingName(false); };
              return (
                <span style={{ display: 'inline-flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <input value={nameDraft.prenom} autoFocus onChange={e => setNameDraft(d => ({ ...d, prenom: e.target.value }))} onKeyDown={keys} placeholder="Prénom" style={inp} />
                  <input value={nameDraft.nom} onChange={e => setNameDraft(d => ({ ...d, nom: e.target.value }))} onKeyDown={keys} placeholder="Nom" style={inp} />
                  <button className="ats-btn ats-btn-primary" onClick={save} style={{ padding: '7px 11px', borderRadius: '7px', fontSize: '12px', fontWeight: 600 }}><BI icon="check-lg" /></button>
                  <button className="ats-btn ats-btn-ghost" onClick={() => setEditingName(false)} style={{ padding: '7px 11px', borderRadius: '7px', fontSize: '12px', fontWeight: 600 }}><BI icon="x-lg" /></button>
                </span>
              );
            })()}
            {c.boond_ref && <span className="ats-mono" style={{ fontSize: '11px', color: 'var(--text-2)', background: 'var(--code-bg)', padding: '2px 7px', borderRadius: '5px' }}>{c.boond_ref}</span>}
            <span style={{ ...statusPill(c.statut, theme) }}>{c.statut || 'Nouveau'}</span>
          </div>
          <div style={{ margin: '6px 0 0' }}>
            <EditField value={c.titre} placeholder="+ titre / poste" displayStyle={{ fontSize: '14px', color: 'var(--text-2)' }}
              onSave={v => onPatch(c.id, { titre: v }, { titre: v })} />
          </div>
          {(() => {
            // L'essentiel d'identité en une ligne — l'âge n'est affiché que si
            // la date de naissance est lisible, jamais deviné.
            const infos = [];
            const t = c.date_naissance ? Date.parse(c.date_naissance) : NaN;
            if (!Number.isNaN(t)) { const age = Math.floor((Date.now() - t) / 31557600000); if (age > 0 && age < 100) infos.push(`${age} ans`); }
            if (c.nationalite) infos.push(c.nationalite);
            if (c.permis) infos.push(`Permis ${c.permis}`);
            return infos.length ? <div style={{ marginTop: '5px', fontSize: '12px', color: 'var(--text-3)' }}>{infos.join(' · ')}</div> : null;
          })()}
          {(() => {
            const puces = splitSkills(c.competences || c.skills);
            if (!puces.length) return null;
            const visibles = puces.slice(0, 8);
            return (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: '8px' }}>
                {visibles.map((sk, k) => (
                  <span key={k} style={{ fontSize: '11px', fontWeight: 600, color: 'var(--chip-fg)', background: 'var(--chip-bg)', padding: '2px 8px', borderRadius: '6px', whiteSpace: 'nowrap' }}>{sk}</span>
                ))}
                {puces.length > visibles.length && (
                  <span title={puces.slice(8).join(' · ')} style={{ fontSize: '11px', fontWeight: 600, color: 'var(--text-3)', padding: '2px 6px' }}>+{puces.length - visibles.length}</span>
                )}
              </div>
            );
          })()}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 'none', alignItems: 'flex-end' }}>
          {/* Anneau de score — la décision se lit avant le détail */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '11px' }}>
            <div className="ats-mono" title="Score de l'analyse du CV pour le matching"
              style={{ width: '56px', height: '56px', flex: 'none', borderRadius: '50%', border: `3px solid ${c.score != null ? scoreShade(c.score) : 'var(--border)'}`, background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1px', color: scoreShade(c.score) }}>
              <strong style={{ fontSize: '16px', fontWeight: 700 }}>{c.score != null ? c.score : '—'}</strong>
              {c.score != null && <span style={{ fontSize: '8px', color: 'var(--text-3)' }}>/100</span>}
            </div>
            <div>
              <div style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-strong)' }}>Score</div>
              <div style={{ fontSize: '10.5px', color: 'var(--text-3)' }}>{c.score != null ? 'analyse du CV' : 'pas encore analysé'}</div>
            </div>
          </div>
          {npc.actif && (
            <div role="status" title={npc.motif || undefined}
              style={{ display: 'flex', alignItems: 'center', gap: '7px', maxWidth: '360px', padding: '6px 10px', borderRadius: '8px', border: '1px solid var(--danger-fg)', color: 'var(--danger-fg)', fontSize: '12px', fontWeight: 600, lineHeight: 1.4 }}>
              <BI icon="slash-circle" />
              <span style={{ minWidth: 0 }}>Ne pas contacter{npc.motif ? <span style={{ fontWeight: 500 }}> — {npc.motif}</span> : null}. Aucun InMail, appel ni invitation ne partira.</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: '8px', flex: 'none', position: 'relative' }}>
          {npc.actif ? (
            <button type="button" className="erp-btn erp-btn--secondary" disabled={npcBusy}
              title="Lever la marque : les envois vers ce candidat redeviennent possibles"
              onClick={async () => {
                // Même texte de confirmation que la carte du besoin : lever la
                // marque doit se lire pareil d'un écran à l'autre.
                const ok = await confirmErpia(confirmationLever(`${c.prenom || ''} ${c.nom || ''}`, npc.motif));
                if (ok) definirNPC(false, null);
              }}>
              <BI icon="unlock" /> Autoriser le contact
            </button>
          ) : (
            <button {...ancre('npc')} type="button" className="erp-btn erp-btn--secondary" disabled={npcBusy}
              title="Marquer « ne pas contacter » : plus aucun InMail, appel IA ni invitation ne partira vers ce candidat"
              onClick={() => setNpcForm(f => (f ? null : { motif: '' }))}>
              <BI icon="slash-circle" /> Ne pas contacter
            </button>
          )}
          {npcForm && !npc.actif && (
            <form onSubmit={(ev) => { ev.preventDefault(); definirNPC(true, npcForm.motif.trim()); }}
              className="erp-card" style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 6, width: '320px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '11px' }}>
              <div style={{ fontSize: '12.5px', fontWeight: 700, color: 'var(--text-strong)' }}>Ne plus contacter {c.prenom || ''} {c.nom || ''}</div>
              <div style={{ fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.45 }}>Le serveur refusera tout InMail, appel de l’agent IA, invitation d’entretien et convocation — y compris en mode automatique du moteur de sourcing.</div>
              <input className="erp-field" autoFocus value={npcForm.motif} placeholder="Motif — demande du candidat, consigne client, litige…"
                onChange={ev => setNpcForm({ motif: ev.target.value })} />
              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={() => setNpcForm(null)} disabled={npcBusy}>Annuler</button>
                <button type="submit" className="erp-btn erp-btn--danger erp-btn--sm" disabled={npcBusy}>{npcBusy ? 'Enregistrement…' : 'Ne pas contacter'}</button>
              </div>
            </form>
          )}
          {/* Fiche marquée : les deux gestes de contact deviennent des boutons
              inertes — un lien mailto: ou tel: contournerait la marque. */}
          {npc.actif ? (
            <>
              <button type="button" className="erp-btn erp-btn--primary" disabled title="Fiche marquée « ne pas contacter »" style={{ opacity: 0.5 }}><BI icon="envelope" /> Contacter</button>
              <button type="button" className="erp-btn erp-btn--secondary" disabled title="Fiche marquée « ne pas contacter »" style={{ opacity: 0.5 }}><BI icon="telephone" /> Appeler</button>
            </>
          ) : (
            <>
              <a href={c.email ? `mailto:${c.email}` : undefined} className="erp-btn erp-btn--primary" style={{ opacity: c.email ? 1 : 0.5 }}><BI icon="envelope" /> Contacter</a>
              <a href={c.telephone ? `tel:${c.telephone}` : undefined} className="erp-btn erp-btn--secondary" style={{ opacity: c.telephone ? 1 : 0.5 }}><BI icon="telephone" /> Appeler</a>
            </>
          )}
          {(() => {
            // Bouton LinkedIn : vrai lien quand un profil est connu (champ dédié ou
            // provenance LinkedIn), grisé + tooltip sinon (il était inerte avant).
            const li = c.linkedin_url || c.linkedin || (sourceUrl && /linkedin\.com/i.test(sourceUrl) ? sourceUrl : null);
            return (
              <a href={li || undefined} target={li ? '_blank' : undefined} rel="noreferrer"
                title={li ? 'Ouvrir le profil LinkedIn' : 'Aucun profil LinkedIn renseigné'}
                className="erp-btn erp-btn--secondary"
                style={{ opacity: li ? 1 : 0.5, cursor: li ? 'pointer' : 'not-allowed' }}>
                <BI icon="linkedin" /> LinkedIn
              </a>
            );
          })()}
          </div>
        </div>
      </div>

      {/* Bandeau de signaux — accroché sous le hero (signature de la refonte).
          Les champs restent ÉDITABLES au clic : mêmes champs et même route que
          l'onglet Administratif, seul le rangement change. */}
      <div style={{ margin: '0 12px', background: 'var(--panel)', border: '1px solid var(--border)', borderTop: 'none', borderRadius: '0 0 10px 10px', padding: '13px 12px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(175px, 1fr))', gap: '10px 0' }}>
        {[
          { icone: 'clock', accent: true, label: 'Disponibilité', corps: (
            <EditField value={c.disponibilite} placeholder="+ disponibilité" displayStyle={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}
              onSave={v => onPatch(c.id, { disponibilite: v }, { disponibilite: v })} />) },
          { icone: 'briefcase', label: 'Situation', corps: (
            <EditField value={c.contrat} placeholder="Pas de contrat actuellement" displayStyle={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}
              onSave={v => onPatch(c.id, { contrat: v }, { contrat: v })} />) },
          { icone: 'cash-coin', label: 'Prétentions', corps: (<>
            <EditField value={c.salaire_souhaite || c.salaire} placeholder="—" displayStyle={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}
              onSave={v => onPatch(c.id, { salaire_souhaite: v }, { salaire_souhaite: v })} />
            <EditField value={c.salaire_actuel} placeholder="+ salaire actuel" displayStyle={{ fontSize: '11px', color: 'var(--text-3)' }}
              format={v => (v ? `actuel : ${v}` : v)}
              onSave={v => onPatch(c.id, { salaire_actuel: v }, { salaire_actuel: v })} />
          </>) },
          { icone: 'geo-alt', label: 'Localisation', corps: (
            <div style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.ville || '—'}</div>) },
          { icone: 'signpost-split', label: 'Source', corps: (
            <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sourceNode}</div>) },
        ].map(t => (
          <div key={t.label} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', minWidth: 0, padding: '2px 10px' }}>
            <span style={{ width: '29px', height: '29px', flex: 'none', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.accent ? 'var(--accent-soft-bg)' : 'var(--chip-bg)', color: t.accent ? 'var(--accent-soft-fg, var(--brand))' : 'var(--text-2)', fontSize: '13px' }}><BI icon={t.icone} /></span>
            <div style={{ minWidth: 0, flex: 1 }}>
              {t.corps}
              <div style={{ fontSize: '9px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-3)', marginTop: '3px' }}>{t.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Le stepper de statut (Nouveau → Vivier) a été retiré le 01.11 : « il
          ne sert à rien » — le statut se lit sur la pastille de l'en-tête. */}

      {/* Bandeau Prochaine action — le prochain RAPPEL, pas la dernière note :
          le bandeau annonçait « prochaine action » en montrant ce qui venait
          d'être fait. Et « Traiter » MARQUE FAIT ; il supprimait la ligne,
          c'est-à-dire la trace qui permet de dire « on l'a déjà relancé ». */}
      {(() => {
        const next = (activities || []).find(a => a.a_faire === true);
        if (!next) return null;
        const n = natureDe(next.nature);
        return (
        <div style={{ marginTop: '14px', background: 'var(--accent-soft-bg)', borderRadius: '11px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '13px' }}>
          <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'var(--surface)', color: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', fontSize: '15px' }}><BI icon={n?.icone || 'bell-fill'} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)' }}>{actText(next)}</div>
            <div style={{ fontSize: '12px', color: next.en_retard ? 'var(--danger-fg)' : 'var(--text-2)', marginTop: '2px' }}>
              {[actAuthor(next), next.echeance ? `${next.en_retard ? 'en retard depuis le' : 'pour le'} ${fmtDate(next.echeance)}` : null].filter(Boolean).join(' · ') || '—'}
            </div>
          </div>
          {next.id != null && <button className="ats-btn ats-btn-primary" onClick={() => traiterActivity(next.id)} style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600, flex: 'none' }}>Traiter</button>}
        </div>
        );
      })()}

      {/* Onglets numérotés : lecture rapide / édition administrative */}
      <div style={{ display: 'flex', gap: '4px', marginTop: '14px', borderBottom: '1px solid var(--hair)' }}>
        {[['apercu', "Vue d'ensemble"], ['carte', 'Carte profil'], ['admin', 'Administratif']].map(([id, lbl], i) => (
          <button key={id} onClick={() => setOngletFiche(id)}
            style={{ fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, padding: '9px 15px 10px', border: 'none', background: 'none', cursor: 'pointer',
              color: ongletFiche === id ? 'var(--text-strong)' : 'var(--text-3)',
              borderBottom: ongletFiche === id ? '2px solid var(--accent)' : '2px solid transparent', marginBottom: '-1px' }}>
            <span className="ats-mono" style={{ fontSize: '10px', marginRight: '7px', color: ongletFiche === id ? 'var(--accent-soft-fg, var(--brand))' : 'var(--text-faint)' }}>0{i + 1}</span>{lbl}
          </button>
        ))}
      </div>

      {/* Inputs fichier cachés partagés entre onglets : « Ajouter » (multi)
          et « Remplacer » servent aussi les pièces de l'onglet Administratif. */}
      <input ref={docFileRef} type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: 'none' }}
        onChange={e => { const fs = Array.from(e.target.files || []); e.target.value = ''; if (fs.length) uploadDocs(fs); }} />
      <input ref={replaceFileRef} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f && replaceTargetDoc) replaceDoc(f, replaceTargetDoc); setReplaceTargetDoc(null); }} />

      {ongletFiche === 'carte' && (
        <CarteProfil candidatId={c.id} onDetail={() => setOngletFiche('apercu')} />
      )}

      {ongletFiche === 'admin' && (
        <div style={{ marginTop: '14px', maxWidth: '760px' }}>{blocAdministratif}</div>
      )}

      {ongletFiche === 'apercu' && (<>
      {/* Grille deux colonnes */}
      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '14px', marginTop: '14px', alignItems: 'start' }}>
        {/* Colonne gauche */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={cardBox}>
            <div style={sectTitle}>Informations</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13.5px' }}><BI icon="envelope" style={{ color: 'var(--text-3)', flex: 'none' }} /><EditField value={c.email} placeholder="+ email" onSave={v => onPatch(c.id, { email: v }, { email: v })} /></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13.5px' }}><BI icon="telephone" style={{ color: 'var(--text-3)', flex: 'none' }} /><EditField value={c.telephone} placeholder="+ téléphone" onSave={v => onPatch(c.id, { telephone: v }, { telephone: v })} /></div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '13.5px' }}><BI icon="geo-alt" style={{ color: 'var(--text-3)', flex: 'none' }} /><EditField value={c.ville} placeholder="+ localisation" onSave={v => onPatch(c.id, { ville: v }, { localisation: v })} /></div>
            </div>
            <div style={{ borderTop: '1px solid var(--hair)', margin: '14px 0' }} />
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px 16px' }}>
              <div><div style={ld}>Resp. Manager</div><div style={vl}>{c.resp_manager || '—'}</div></div>
              <div><div style={ld}>Resp. RH</div><div style={vl}>{c.resp_rh || '—'}</div></div>
              <div style={{ gridColumn: '1 / -1' }}><div style={ld}>Agence</div><div style={vl}>{c.agence || '—'}</div></div>
              <div><div style={ld}>Source</div><div style={vl}>{sourceNode}</div></div>
              {/* La fiche dit QUAND elle est née et PAR QUI — le nom vient du
                  serveur (utilisateur, sinon origine lisible) ; sans lui, la
                  case garde la date seule plutôt qu'un « inconnu ». */}
              <div><div style={ld}>Ajouté le</div><div style={vl}>{fmtDate(c.created_at)}{(c.created_by_name || creePar) ? <span style={{ color: 'var(--text-3)', fontWeight: 400 }}> · par {c.created_by_name || creePar}</span> : null}</div></div>
            </div>
          </div>

          {c.resume && (
            <div style={cardBox}>
              <div style={sectTitle}>Résumé</div>
              <p style={{ margin: 0, fontSize: '13px', lineHeight: 1.6, color: 'var(--text)' }}>{c.resume}</p>
            </div>
          )}
          <div style={cardBox}>
            <div style={sectTitle}>Compétences</div>
            <SkillsEditor value={c.competences || c.skills} onSave={v => onPatch(c.id, { competences: v }, { competences: v })} />
          </div>
          {/* Étape pipeline */}
          <div style={cardBox}>
            <div style={sectTitle}>Étape du pipeline</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {STEPS.map(s => {
                const active = s === (c.statut || 'Nouveau'); const col = statusColors(s, theme);
                return <button key={s} onClick={() => { onChangeStatut(c.id, s); setTimeout(loadTimeline, 800); }} style={{ fontFamily: 'inherit', padding: '7px 12px', borderRadius: '8px', fontSize: '12.5px', fontWeight: active ? 600 : 500, cursor: 'pointer', border: active ? '1px solid transparent' : '1px solid var(--input-border)', background: active ? col.bg : 'var(--ghost-bg)', color: active ? col.fg : 'var(--text-2)' }}>{s}</button>;
              })}
            </div>
          </div>
        </div>

        {/* Colonne droite */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {/* Positionnements */}
          <div style={cardBox}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: ((positions || []).length ? 12 : 0) }}>
              <span style={{ ...sectTitle, marginBottom: 0 }}>Positionnements</span>
              <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '11px', color: 'var(--text-2)', background: 'var(--chip-bg)', padding: '1px 7px', borderRadius: '6px' }}>{positions == null ? '…' : (positions || []).length}</span>
              {/* L'optimiseur de CV vit sur la vue besoin — plus de bouton ici. */}
              <button className="ats-btn ats-btn-ghost" onClick={() => setShowPositionner(true)} style={{ marginLeft: 'auto', padding: '6px 11px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600 }}><BI icon="plus-lg" /> Positionner</button>
            </div>
            {positions == null ? (
              <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement…</div>
            ) : (positions || []).length === 0 ? (
              <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun positionnement sur un besoin.</div>
            ) : positions.map((p, i) => {
              const bid = p.besoin_id ?? p.id ?? p.besoinId;
              // cv_designe est la source de vérité ; les anciens champs restent
              // lus pour les réponses qui ne le portent pas encore.
              // Le CV qui PART pour ce besoin est choisi par le serveur, avec la
              // règle de la vue besoin et du dossier AO (`cv_retenu`) : désigné,
              // sinon le CV format entreprise du besoin. Sans lui, la fiche disait
              // « aucun CV propre à ce besoin » quand la vue besoin en montrait un.
              const retenu = p.cv_retenu && p.cv_retenu.propre_au_besoin ? p.cv_retenu : null;
              const hasCv = p.cv_designe === true || p.has_cv_besoin || p.cv_document_id != null || !!retenu;
              const cvLabel = p.cv_label || retenu?.nom || null;
              // Documents du positionnement (dont « CV TAT préparé », versionné) — forme tolérante :
              // liste de groupes { besoin_id, documents } / docs plats avec besoin_id / objet indexé.
              const pDocs = (() => {
                if (positionDocs == null || bid == null) return [];
                if (Array.isArray(positionDocs)) {
                  const out = [];
                  for (const g of positionDocs) {
                    if (String(g.besoin_id ?? g.besoinId) !== String(bid)) continue;
                    if (Array.isArray(g.documents)) out.push(...g.documents); else out.push(g);
                  }
                  return out;
                }
                const grp = positionDocs[bid] ?? positionDocs[String(bid)];
                return Array.isArray(grp) ? grp : (Array.isArray(grp?.documents) ? grp.documents : []);
              })();
              return (
              <div key={i} style={{ padding: '10px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hair)' }}>
                <div onClick={() => bid != null && onOpenBesoin?.(bid)} style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: bid != null ? 'pointer' : 'default' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.titre || p.besoin_titre || p.poste || '—'}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-2)' }}>{p.client || p.sous_titre || p.reference || p.ref || ''}</div>
                    {/* QUI a mis ce profil sur CE besoin, et par quel chemin.
                        La phrase arrive faite du serveur — le front ne la
                        recompose pas. Absente sur les positionnements
                        d'avant la trace : la ligne disparaît, elle n'affiche
                        pas « inconnu ». */}
                    {p.positionne_lisible && (
                      <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.09em', textTransform: 'uppercase', color: 'var(--text-3)', marginRight: '6px' }}>Ajouté</span>
                        {p.positionne_lisible}
                      </div>
                    )}
                  </div>
                  {p.statut && <span style={statusPill(p.statut, theme)}>{p.statut}</span>}
                  {(p.date || p.created_at) && <span style={{ fontSize: '11.5px', color: 'var(--text-faint)', fontFamily: "'IBM Plex Mono',monospace", flex: 'none' }}>{fmtDate(p.date || p.created_at)}</span>}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '7px' }}>
                  {hasCv
                    ? <span title={retenu && !(p.cv_designe === true || p.has_cv_besoin || p.cv_document_id != null) ? `${retenu.libelle || 'CV de ce besoin'} — le même dans le dossier AO` : (cvLabel ? `CV désigné : ${cvLabel}` : undefined)} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, color: '#16a34a', background: 'rgba(22,163,74,0.12)', padding: '2px 8px', borderRadius: '6px' }}><BI icon="file-earmark-check" /> CV pour ce besoin{cvLabel ? ` : ${cvLabel}` : ''}</span>
                    : (cvDocs.length > 0
                      // Pas de CV propre à ce besoin : c'est celui de la fiche
                      // qui sera utilisé partout (matching, dossier AO).
                      ? (() => {
                        // Le CV de la fiche est celui qui sera lu : autant
                        // pouvoir l'ouvrir d'ici — le principal, sinon le plus
                        // récent des CV originaux.
                        const cvFiche = cvDocs.find(d => d.principal) || cvDocs[0];
                        return (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                            <span title="Aucun CV spécifique n'a été choisi pour ce besoin : le CV de la fiche sera utilisé (matching, dossier AO). « Définir le CV » permet d'en choisir un autre." style={{ fontSize: '11.5px', color: 'var(--text-faint)' }}>CV de la fiche (aucun CV propre à ce besoin)</span>
                            {cvFiche?.id != null && (
                              <button className="ats-edit-pen" title={`Voir ${cvFiche.name || 'le CV de la fiche'}`}
                                onClick={() => setViewerDoc({ docId: cvFiche.id, label: cvFiche.name || 'CV de la fiche', estCv: true })}
                                style={{ flex: 'none' }}><BI icon="eye" /></button>
                            )}
                          </span>
                        );
                      })()
                      : <span title="Ce candidat n'a aucun CV : ajoutez-en un dans la section Documents, ou choisissez-en un via « Définir le CV »." style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, color: '#d97706', background: 'rgba(217,119,6,0.12)', padding: '2px 8px', borderRadius: '6px' }}><BI icon="exclamation-triangle" /> Aucun CV sur la fiche</span>)}
                  {bid != null && <button className="ats-btn ats-btn-ghost" onClick={() => { setCvPickerTous(false); setCvPickerBesoin({ besoinId: bid, titre: p.titre || p.besoin_titre || 'ce besoin', hasCv }); }} style={{ marginLeft: 'auto', padding: '4px 9px', borderRadius: '6px', fontSize: '11.5px', fontWeight: 600 }}><BI icon="file-earmark-text" /> {hasCv ? 'Changer le CV' : 'Définir le CV'}</button>}
                </div>
                {/* CV désigné sans pièce de positionnement : le document vit sur
                    la fiche (désignation d'un document existant). Sans cette
                    ligne, le badge nomme un CV qu'on ne peut ni voir ni
                    télécharger — c'est le « parfois » du bug signalé. */}
                {(() => {
                  const cvId = p.cv_document_id ?? p.cvDocumentId ?? null;
                  if (!hasCv || cvId == null) return null;
                  const dejaListe = pDocs.some(d => String(d.candidate_document_id ?? d.candidateDocumentId ?? d.document_id ?? '') === String(cvId));
                  if (dejaListe) return null;
                  const nom = deBrand(cvLabel || 'CV désigné pour ce besoin');
                  return (
                    <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11.5px', color: 'var(--text-2)' }}>
                      <BI icon="file-earmark-check" style={{ color: '#16a34a', flex: 'none' }} />
                      <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{nom}</span>
                      <button className="ats-edit-pen" title="Aperçu" onClick={() => setViewerDoc({ docId: cvId, label: nom, estCv: true })} style={{ flex: 'none' }}><BI icon="eye" /></button>
                      <TelechargerDoc href={avecCle(`${BASE}/candidates/${c.id}/documents/${cvId}/preview?download=1`)}
                        office={estDocOffice({ est_cv: true, file_name: cvLabel || '' })} />
                    </div>
                  );
                })()}
                {pDocs.length > 0 && (
                  <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', gap: '3px' }}>
                    {pDocs.map((d, k) => {
                      // Aperçu TOUJOURS via le back (jamais l'URL kDrive brute → « Failed to
                      // fetch » CORS). Si la pièce référence un candidate_document, on ouvre le
                      // viewer canonique candidate_documents ; sinon le viewer canonique sur
                      // l'endpoint position-documents (docx→pdf converti côté serveur).
                      const cdId = d.candidate_document_id ?? d.candidateDocumentId ?? d.document_id ?? null;
                      const pdId = d.id ?? d.position_document_id ?? null;
                      // « maj {date} par {qui} » si écrasé (version > 1), sinon « ajouté le {date} »
                      const dateInfo = (() => {
                        const fmt = (iso) => { const t = fmtDateHeure(iso); return t || null; };
                        if ((d.version ?? 1) > 1 && d.updated_at) { const t = fmt(d.updated_at); return t ? `maj ${t}${d.updated_by_name ? ` par ${d.updated_by_name}` : ''}` : null; }
                        const t = fmt(d.created_at ?? d.uploaded_at); return t ? `ajouté le ${t}` : null;
                      })();
                      const estDesigne = p.cv_document_id != null
                        && String(d.candidate_document_id ?? d.candidateDocumentId ?? d.document_id ?? '') === String(p.cv_document_id);
                      return (
                      <div key={d.id ?? k} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '11.5px', color: 'var(--text-2)' }}>
                        <BI icon="file-earmark-text" style={{ color: estDesigne ? '#16a34a' : 'var(--text-3)', flex: 'none' }} />
                        <span style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{deBrand(d.label || d.name || d.original_name || 'Document')}</span>
                        {estDesigne && (
                          <span title="C'est ce document que le matching lit et que le dossier AO présente pour ce besoin."
                            style={{ flex: 'none', fontSize: '10px', fontWeight: 700, padding: '1px 7px', borderRadius: '5px', background: 'rgba(22,163,74,0.12)', color: '#16a34a' }}>désigné</span>
                        )}
                        {dateInfo && <span style={{ flex: 'none', fontSize: '10.5px', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>{dateInfo}</span>}
                        {d.version != null && <span className="ats-mono" title={dateInfo || undefined} style={{ flex: 'none', fontSize: '10.5px', color: 'var(--text-3)', background: 'var(--chip-bg)', padding: '1px 6px', borderRadius: '5px' }}>v{d.version}</span>}
                        {cdId != null
                          ? <button className="ats-edit-pen" title="Aperçu" onClick={() => setViewerDoc({ docId: cdId, label: d.label || d.name || 'Document', source: d.source, kind: d.kind })} style={{ flex: 'none' }}><BI icon="eye" /></button>
                          : pdId != null
                            ? <button className="ats-edit-pen" title="Aperçu" onClick={() => setViewerDoc({ previewBase: `${BASE}/position-documents/${pdId}/preview`, label: d.label || d.name || 'Document', source: d.source, kind: d.kind })} style={{ flex: 'none' }}><BI icon="eye" /></button>
                            : null}
                        {cdId != null
                          ? <TelechargerDoc href={avecCle(`${BASE}/candidates/${c.id}/documents/${cdId}/preview?download=1`)} office={estDocOffice(d)} nom={d.original_name || d.file_name || d.name || d.label || ''} />
                          : pdId != null
                            // format=word|pdf est servi sur cette route depuis
                            // la PR #278 back — même contrat que la route candidats.
                            ? <TelechargerDoc href={avecCle(`${BASE}/position-documents/${pdId}/preview?download=1`)} office={estDocOffice(d)} nom={d.original_name || d.file_name || d.name || d.label || ''} />
                            : null}
                        {pdId != null && (
                          <button className="ats-edit-pen" title="Supprimer cette pièce du besoin (la fiche candidat n'est pas touchée)"
                            disabled={delPosDoc === pdId} onClick={() => deletePositionDoc(d, bid)}
                            style={{ flex: 'none', color: 'var(--danger-fg)' }}>
                            <BI icon={delPosDoc === pdId ? 'arrow-repeat' : 'trash3'} style={delPosDoc === pdId ? { animation: 'ats-spin .7s linear infinite' } : undefined} />
                          </button>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
              );
            })}
          </div>

          {/* Signatures électroniques (signd) — masqué si le flag org est désactivé */}
          {docsignOn && <SignaturesBlock candidateId={c.id} candidateEmail={c.email} showNotification={showNotification} cardBox={cardBox} sectTitle={sectTitle} />}

          {/* Actions / Rappels */}
          <div style={cardBox}>
            {(() => {
            const acts = activities || [];
            return (<>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
              <div {...ancre('actions')} style={{ display: 'flex', alignItems: 'center', gap: '9px' }}>
                <span style={sectTitle}>Actions / Rappels</span>
                <span className="ats-mono" style={{ fontSize: '11px', color: 'var(--text-2)', background: 'var(--chip-bg)', padding: '1px 7px', borderRadius: '6px' }}>{acts.length}</span>
              </div>
              {!todoForm && <button className="ats-btn ats-btn-ghost" onClick={() => openTodo()} style={{ padding: '6px 11px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600 }}>+ Ajouter</button>}
            </div>
            {/* LA SAISIE TIENT EN DEUX LIGNES, TROIS POUR UN RAPPEL.
                Le temps (déjà fait / à faire) et la nature du geste partagent
                la même ligne ; la ligne « Échéance » n'existe QUE pour un
                rappel — c'est le serveur qui dit lequel des deux temps la
                demande (`echeance` du vocabulaire), l'écran ne le devine pas.
                C'est ce qui rend la saisie d'un appel déjà passé immédiate :
                un mot, deux clics, entrée. */}
            {todoForm && (() => {
              const tps = tempsDe(todoForm.temps);
              const aFaire = tps.echeance === true;
              const chip = (sel) => ({ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '5px 10px', borderRadius: '8px', fontSize: '12px', fontFamily: 'inherit', cursor: 'pointer', border: sel ? '1px solid var(--text-strong)' : '1px solid var(--input-border)', background: sel ? 'var(--ghost-bg-hover)' : 'transparent', color: sel ? 'var(--text-strong)' : 'var(--text)', fontWeight: sel ? 600 : 500 });
              return (
              <div style={{ background: 'var(--ghost-bg)', border: '1px solid var(--border)', borderRadius: '11px', padding: '14px', marginBottom: '12px' }}>
                <input className="ats-edit-input" autoFocus value={todoForm.texte}
                  onChange={e => setTodoForm(f => ({ ...f, texte: e.target.value }))}
                  onKeyDown={e => { if (e.key === 'Enter') saveTodo(); }}
                  placeholder={aFaire ? 'Que faut-il faire ?' : "Qu'est-ce qui s'est passé ?"} />
                <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '6px', marginTop: '11px' }}>
                  {(vocab.temps || []).map(x => (
                    <button key={x.cle} type="button" onClick={() => setTodoForm(f => ({ ...f, temps: x.cle }))}
                      title={x.aide || undefined} style={chip(todoForm.temps === x.cle)}>{x.libelle}</button>
                  ))}
                  <span style={{ width: '1px', alignSelf: 'stretch', background: 'var(--hair)', margin: '0 4px' }} />
                  {(vocab.natures || []).map(n => (
                    <button key={n.cle} type="button" onClick={() => setTodoForm(f => ({ ...f, nature: n.cle }))}
                      title={aFaire ? n.libelle : n.passe} style={chip(todoForm.nature === n.cle)}>
                      <BI icon={n.icone} /> {aFaire ? n.libelle : n.passe}
                    </button>
                  ))}
                </div>
                {aFaire && (<>
                  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-3)', margin: '12px 0 7px' }}>Échéance</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {(vocab.delais || []).map(d => (
                      <button key={d.cle} type="button" onClick={() => setTodoForm(f => ({ ...f, echeance: d.cle }))}
                        style={chip(todoForm.echeance === d.cle)}>{d.libelle}</button>
                    ))}
                    <input type="date" value={/^\d{4}-\d{2}-\d{2}$/.test(todoForm.echeance || '') ? todoForm.echeance : ''}
                      onChange={e => e.target.value && setTodoForm(f => ({ ...f, echeance: e.target.value }))}
                      title="Une autre date" style={{ ...chip(/^\d{4}-\d{2}-\d{2}$/.test(todoForm.echeance || '')), padding: '4px 9px' }} />
                  </div>
                </>)}
                <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <BI icon="person-check" />
                  {aFaire
                    ? "Créé par vous (l'auteur est enregistré automatiquement)."
                    : "Enregistré comme fait aujourd'hui, par vous — une action faite n'a pas d'échéance."}
                </div>
                <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                  <button className="ats-btn ats-btn-primary" onClick={saveTodo} disabled={actSaving || !todoForm.texte.trim()} style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600, opacity: (actSaving || !todoForm.texte.trim()) ? 0.6 : 1 }}>{actSaving ? 'Enregistrement…' : (aFaire ? 'Poser le rappel' : 'Noter')}</button>
                  <button className="ats-btn ats-btn-ghost" onClick={() => setTodoForm(null)} disabled={actSaving} style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600 }}>Annuler</button>
                </div>
              </div>
              );
            })()}
            {acts.length === 0 && !todoForm ? (
              <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucune action ni rappel.</div>
            ) : acts.map((a, i) => {
              const boond = actIsBoond(a); const manual = actIsManual(a);
              // L'icône du geste porte l'information : on lit « appel » sans
              // lire la ligne. Une entrée d'avant la nature n'en a pas — elle
              // reste neutre plutôt que d'être rangée dans une case devinée.
              const n = natureDe(a.nature);
              const aFaire = a.a_faire === true;
              const retard = a.en_retard === true;
              return (
              <div key={a.id ?? i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '10px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hair)' }}>
                <div title={n ? (aFaire ? n.libelle : n.passe) : undefined}
                  style={{ width: '26px', height: '26px', borderRadius: '7px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', marginTop: '1px', background: aFaire ? 'var(--accent-soft-bg)' : 'var(--chip-bg)', color: aFaire ? 'var(--text-strong)' : 'var(--text-3)' }}>
                  <BI icon={n?.icone || (aFaire ? 'bell' : 'dot')} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', color: 'var(--text)' }}>{actText(a)}</div>
                  <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '2px' }}>{[actAuthor(a), aFaire ? (a.echeance ? `à faire pour le ${fmtDate(a.echeance)}` : 'à faire') : (actDate(a) ? fmtDate(actDate(a)) : null)].filter(Boolean).join(' · ') || '—'}</div>
                </div>
                {retard && <span title="L'échéance est passée" style={{ fontSize: '10px', fontWeight: 700, padding: '1px 6px', borderRadius: '5px', background: 'var(--danger-bg, rgba(226,35,26,0.14))', color: 'var(--danger-fg)', flex: 'none', whiteSpace: 'nowrap', marginTop: '1px' }}>en retard</span>}
                {aFaire && a.id != null && <button className="ats-btn ats-btn-ghost" onClick={() => traiterActivity(a.id)} title="Marquer comme fait — la ligne reste dans l'historique" style={{ flex: 'none', padding: '4px 10px', borderRadius: '7px', fontSize: '11.5px', fontWeight: 600 }}>Fait</button>}
                {boond && <span style={{ fontSize: '10px', fontWeight: 600, padding: '1px 6px', borderRadius: '5px', background: 'var(--chip-bg)', color: 'var(--text-2)', flex: 'none', whiteSpace: 'nowrap', marginTop: '1px' }}>Boond</span>}
                {manual && a.id != null && <button className="ats-edit-pen" title="Supprimer" onClick={() => removeActivity(a.id)} style={{ color: 'var(--danger-fg)', flex: 'none' }}><BI icon="trash3" /></button>}
              </div>
              );
            })}
            </>);
            })()}
          </div>

          {/* Discussion interne */}
          <div style={cardBox}>
            <div style={sectTitle}>Discussion interne</div>
            {comments == null ? (
              <div style={{ fontSize: '12.5px', color: 'var(--text-faint)', paddingBottom: '10px' }}>Chargement…</div>
            ) : comments.length === 0 ? (
              <div style={{ border: '1px dashed var(--border)', borderRadius: '9px', padding: '16px', textAlign: 'center', fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun commentaire. Tapez <b>@</b> pour mentionner un collègue.</div>
            ) : comments.map((cm, i) => {
              const author = cm.author_username || cm.author || cm.username || '—';
              const mine = me && (author === me.username);
              const canDelete = mine || me?.admin;
              return (
              <div key={cm.id ?? i} style={{ display: 'flex', gap: '10px', marginBottom: '12px' }}>
                <div className="ats-mono" style={{ width: '32px', height: '32px', borderRadius: '8px', flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 600, background: mine ? 'var(--text-strong)' : 'var(--accent-soft-bg)', color: mine ? 'var(--bg)' : 'var(--text-strong)' }}>{teamInitials(author)}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12.5px' }}><b style={{ color: 'var(--text-strong)' }}>{author}</b> <span style={{ color: 'var(--text-faint)', fontSize: '11.5px' }}>{fmtDate(cm.created_at || cm.createdAt || cm.date)}</span></span>
                    {canDelete && <button className="ats-edit-pen" title="Supprimer" onClick={() => deleteComment(cm)} style={{ marginLeft: 'auto', color: 'var(--danger-fg, var(--brand))' }}><BI icon="trash3" /></button>}
                  </div>
                  <div style={{ fontSize: '13px', color: 'var(--text)', marginTop: '2px', lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>{renderWithMentions(cm.body)}</div>
                </div>
              </div>
              );
            })}
            <div style={{ position: 'relative', marginTop: '4px' }}>
              <textarea ref={draftRef} className="ats-edit-input" value={draft} onChange={onDraftChange} rows={2} placeholder="Écrire un commentaire… tapez @ pour mentionner" />
              {mention && filtered.length > 0 && (
                <div style={{ position: 'absolute', left: 0, bottom: '100%', marginBottom: '6px', width: '240px', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '10px', boxShadow: '0 8px 28px var(--hover-shadow)', overflow: 'hidden', zIndex: 5 }}>
                  {filtered.map(t => (
                    <button key={t.id} onMouseDown={e => { e.preventDefault(); insertMention(t); }} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '9px', padding: '8px 11px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', color: 'var(--text)' }}>
                      <span className="ats-mono" style={{ width: '24px', height: '24px', borderRadius: '6px', background: 'var(--accent-soft-bg)', color: 'var(--text-strong)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: 600 }}>{teamInitials(t.username)}</span>
                      <span style={{ fontSize: '13px' }}>@{t.username}</span>{t.email && <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>{t.email}</span>}
                    </button>
                  ))}
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '8px' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>Les personnes mentionnées sont notifiées.</span>
                <button className="ats-btn ats-btn-primary" onClick={publish} disabled={publishing || !draft.trim()} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600, opacity: (publishing || !draft.trim()) ? 0.6 : 1 }}><BI icon={publishing ? 'arrow-repeat' : 'send'} style={publishing ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> {publishing ? 'Publication…' : 'Publier'}</button>
              </div>
            </div>
          </div>

          {/* Activité — historique ERP automatique (positionnements, statut…), lecture seule */}
          <div style={cardBox}>
            <div style={{ ...sectTitle }}>Activité</div>
            {(() => {
              const list = (Array.isArray(timeline) ? timeline : []).filter(t => !estActiviteAutoSignature(actText(t)));
              const events = list.length ? list.map(t => ({
                id: t.id, text: actText(t), author: actAuthor(t), date: actDate(t),
              })) : [
                { text: `Statut actuel : ${c.statut || 'Nouveau'}`, date: null, synthetic: true },
                { text: `Profil ajouté · ${source}`, date: c.created_at, synthetic: true },
              ];
              return events.map((ev, k, arr) => (
                <div key={ev.id ?? `s${k}`} style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 'none' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '2px', background: 'var(--brand)', marginTop: '4px' }} />
                    {k < arr.length - 1 && <div style={{ width: '2px', flex: 1, background: 'var(--border)', minHeight: '14px' }} />}
                  </div>
                  <div style={{ paddingBottom: '14px', flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text)' }}>{ev.text}</div>
                    <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '2px' }}>
                      {[ev.author, ev.date ? fmtDate(ev.date) : null].filter(Boolean).join(' · ') || 'Mis à jour récemment'}
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>

          {/* Documents — la carte entière accepte le glisser-déposer :
              chaque fichier lâché part par la même route que « Ajouter un
              document » (un envoi par fichier, bilan par fichier). */}
          <div style={{ ...cardBox, ...(dropDocs ? { outline: '2px dashed var(--brand)', outlineOffset: '-2px', background: 'rgba(226,35,26,0.03)' } : {}), position: 'relative' }}
            onDragOver={e => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDropDocs(true); } }}
            onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropDocs(false); }}
            onDrop={e => { e.preventDefault(); setDropDocs(false); const fs = Array.from(e.dataTransfer?.files || []); if (fs.length) uploadDocs(fs); }}>
            {dropDocs && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.72)', zIndex: 3, pointerEvents: 'none', borderRadius: 'inherit' }}>
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--brand)' }}>
                  Déposez les fichiers ici — ils rejoignent les documents du candidat
                </span>
              </div>
            )}
            <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
              onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) uploadCv(f); }} />
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', gap: '8px', flexWrap: 'wrap' }}>
              <span style={{ ...sectTitle, marginBottom: 0, display: 'inline-flex', alignItems: 'center', gap: '9px' }}>
                Documents
                {cvBadge && <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '10.5px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: cvBadge.bg, color: cvBadge.fg, textTransform: 'none', letterSpacing: 0 }}><BI icon="clock-history" /> {cvBadge.label}</span>}
              </span>
              <div style={{ display: 'flex', gap: '7px', flexWrap: 'wrap' }}>
                {c.boond_ref && (
                  <button className="ats-btn ats-btn-ghost" onClick={importFromBoond} disabled={boondImporting} style={{ padding: '6px 11px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600, opacity: boondImporting ? 0.6 : 1 }}><BI icon={boondImporting ? 'arrow-repeat' : 'cloud-download'} /> {boondImporting ? 'Import…' : 'Importer depuis Boond'}</button>
                )}
                <button className="ats-btn ats-btn-ghost" onClick={() => fileInputRef.current?.click()} disabled={uploadingCv} style={{ padding: '6px 11px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600, opacity: uploadingCv ? 0.6 : 1 }}><BI icon={uploadingCv ? 'arrow-repeat' : 'cloud-upload'} /> {uploadingCv ? 'Upload…' : 'Uploader un CV'}</button>
                <button className="ats-btn ats-btn-ghost" onClick={() => docFileRef.current?.click()} disabled={uploadingDoc} style={{ padding: '6px 11px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600, opacity: uploadingDoc ? 0.6 : 1 }}><BI icon={uploadingDoc ? 'arrow-repeat' : 'file-earmark-plus'} /> {uploadingDoc ? 'Ajout…' : 'Ajouter un document'}</button>
                <button className="ats-btn ats-btn-ghost" onClick={reanalyzeCV} disabled={reanalyzing || cvDocs.length === 0} style={{ padding: '6px 11px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600, opacity: (reanalyzing || cvDocs.length === 0) ? 0.5 : 1 }}><BI icon={reanalyzing ? 'arrow-repeat' : 'stars'} /> {reanalyzing ? 'Analyse…' : 'Ré-analyser'}</button>
              </div>
            </div>

            {/* CV — le principal (source IA & matching) dans une section à part, puis les autres CV */}
            {(() => {
              const cvRow = (d, i, { highlighted, canSetPrincipal }) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '10px 12px', border: highlighted ? '1px solid var(--brand)' : '1px solid var(--border)', background: highlighted ? 'rgba(226,35,26,0.05)' : 'transparent', borderRadius: '9px', marginBottom: '8px' }}>
                  <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(226,35,26,0.12)', color: 'var(--brand)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', fontSize: '15px' }}><BI icon="file-earmark-pdf" /></div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dispName(d, d.name)}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{docDateInfo(d) || (d.date ? fmtDate(d.date) : 'CV original')}</div>
                  </div>
                  {canSetPrincipal && d.id != null && <button className="ats-edit-pen" title="Définir comme CV principal" onClick={() => setPrincipalCv(d.id)}><BI icon="star" /></button>}
                  {d.id != null && <button className="ats-edit-pen" title="Aperçu" onClick={() => setViewerDoc({ docId: d.id, label: d.name, estCv: true })}><BI icon="eye" /></button>}
                  {d.id != null && <button className="ats-edit-pen" title="Renommer" onClick={() => renameDoc(d.id, d.name)}><BI icon="pencil" /></button>}
                  {d.id != null && <button className="ats-edit-pen" title="Remplacer par un nouveau fichier" disabled={replacingId === d.id} onClick={() => { setReplaceTargetDoc({ id: d.id, type_piece: 'cv', label: d.name }); replaceFileRef.current?.click(); }}><BI icon={replacingId === d.id ? 'arrow-repeat' : 'arrow-clockwise'} style={replacingId === d.id ? { animation: 'ats-spin .7s linear infinite' } : undefined} /></button>}
                  <TelechargerDoc href={docDownloadHref(d)} office={estDocOffice(d)} nom={d.original_name || d.file_name || d.name || d.label || ''} />
                  {d.id != null && <button className="ats-edit-pen" title="Supprimer" onClick={() => deleteDoc(d.id, d.name)} style={{ color: 'var(--danger-fg)' }}><BI icon="trash3" /></button>}
                </div>
              );
              if (cvDocs.length === 0) return (
                <div style={{ display: 'flex', alignItems: 'center', gap: '11px', padding: '12px 14px', border: '1px dashed var(--border)', borderRadius: '9px', color: 'var(--text-3)', fontSize: '13px' }}>
                  <div style={{ width: '36px', height: '36px', borderRadius: '8px', background: 'var(--chip-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', fontSize: '16px' }}><BI icon="file-earmark-arrow-up" /></div>
                  <span>Aucun CV. <b style={{ cursor: 'pointer', color: 'var(--text-strong)' }} onClick={() => fileInputRef.current?.click()}>Uploader un CV</b> pour activer l'analyse IA et la génération du format entreprise.</span>
                </div>
              );
              return (
                <>
                  {principalCv && (
                    <>
                      <div style={{ fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '6px', display: 'inline-flex', alignItems: 'center', gap: '7px' }}>
                        CV principal
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '9.5px', fontWeight: 700, padding: '1px 7px', borderRadius: '5px', background: 'rgba(226,35,26,0.12)', color: 'var(--brand)', textTransform: 'none', letterSpacing: 0 }}><BI icon="star-fill" /> Principal</span>
                      </div>
                      {cvRow(principalCv, 'principal', { highlighted: true })}
                      <div style={{ fontSize: '11px', color: 'var(--text-faint)', margin: '-2px 0 12px', display: 'flex', alignItems: 'center', gap: '5px' }}><BI icon="info-circle" /> Source de l'analyse IA (compétences, résumé) et du matching AO.</div>
                    </>
                  )}
                  {otherCvs.length > 0 && (
                    <>
                      <div style={{ fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '6px' }}>Autres CV</div>
                      {otherCvs.map((d, i) => cvRow(d, i, { canSetPrincipal: true }))}
                    </>
                  )}
                </>
              );
            })()}

            {/* Générer CV format entreprise */}
            <button className="ats-btn ats-btn-primary" onClick={generateTatCv} disabled={cvDocs.length === 0}
              title={cvDocs.length === 0 ? 'Ajoute un CV original d\'abord' : 'Mettre en forme au format entreprise (TAT)'}
              style={{ width: '100%', justifyContent: 'center', display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600, marginTop: '4px', opacity: cvDocs.length === 0 ? 0.5 : 1, cursor: cvDocs.length === 0 ? 'not-allowed' : 'pointer' }}>
              <BI icon="file-earmark-richtext" /> Générer le CV format entreprise
            </button>

            {/* Documents générés */}
            {genDocs.length > 0 && (
              <div style={{ marginTop: '12px', borderTop: '1px solid var(--hair)', paddingTop: '10px' }}>
                {genDocs.map((d, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '7px 0' }}>
                    <BI icon="file-earmark-check" style={{ color: '#16a34a', flex: 'none' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '12.5px', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{dispName(d, genLabel(d))}</div>
                      {docDateInfo(d) && <div style={{ fontSize: '11px', color: 'var(--text-3)' }}>{docDateInfo(d)}</div>}
                    </div>
                    {/* Rendre le CV entreprise utilisable pour un besoin sans avoir
                        à repasser par « Définir le CV » du positionnement. */}
                    {d.id != null && genLabel(d) === 'CV format entreprise' && (positions || []).length > 0 && (
                      <button className="ats-edit-pen" title="Utiliser ce CV pour un de ses besoins"
                        onClick={() => setUseForBesoin(d)}><BI icon="briefcase" /></button>
                    )}
                    {d.id != null && <button className="ats-edit-pen" title="Aperçu" onClick={() => setViewerDoc({ docId: d.id, label: dispName(d, genLabel(d)), source: d.source, kind: d.kind })}><BI icon="eye" /></button>}
                    {d.id != null && <button className="ats-edit-pen" title="Renommer" onClick={() => renameDoc(d.id, dispName(d, d.name || d.original_name || genLabel(d)))}><BI icon="pencil" /></button>}
                    {d.id != null && <button className="ats-edit-pen" title="Remplacer par un nouveau fichier" disabled={replacingId === d.id} onClick={() => { setReplaceTargetDoc(d); replaceFileRef.current?.click(); }}><BI icon={replacingId === d.id ? 'arrow-repeat' : 'arrow-clockwise'} style={replacingId === d.id ? { animation: 'ats-spin .7s linear infinite' } : undefined} /></button>}
                    <TelechargerDoc href={docDownloadHref(d)} office={estDocOffice(d)} nom={d.original_name || d.file_name || d.name || d.label || ''} />
                    {d.id != null && <button className="ats-edit-pen" title="Supprimer" onClick={() => deleteDoc(d.id, genLabel(d))} style={{ color: 'var(--danger-fg)' }}><BI icon="trash3" /></button>}
                  </div>
                ))}
              </div>
            )}

            {/* Autres documents (jamais masquer une pièce renvoyée) */}
            {otherDocs.length > 0 && (
              <div style={{ marginTop: '12px', borderTop: '1px solid var(--hair)', paddingTop: '10px' }}>
                <div style={{ fontSize: '10.5px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-3)', marginBottom: '6px' }}>Autres documents</div>
                {otherDocs.map((d, i) => ligneDocSimple(d, i))}
              </div>
            )}

            {/* Les pièces administratives ont déménagé : on le dit au lieu de
                laisser croire qu'elles ont disparu. */}
            {adminDocs.length > 0 && (
              <div style={{ marginTop: '12px', borderTop: '1px solid var(--hair)', paddingTop: '10px', fontSize: '11.5px', color: 'var(--text-3)' }}>
                <BI icon="folder-symlink" style={{ marginRight: '6px' }} />
                {adminDocs.length} pièce{adminDocs.length > 1 ? 's' : ''} administrative{adminDocs.length > 1 ? 's' : ''} (identité, permis, AVS…) — voir l'onglet{' '}
                <b style={{ cursor: 'pointer', color: 'var(--text-strong)' }} onClick={() => setOngletFiche('admin')}>Administratif</b>.
              </div>
            )}
          </div>
        </div>
      </div>
      </>)}

      {confirmElement}

      {/* Modale Générer le contrat */}
      {showContract && <ContratModal candidat={c} onClose={() => setShowContract(false)} showNotification={showNotification} />}

      {/* Modale Positionner sur un besoin */}
      {showPositionner && <AssignMatchingModal mode="pickBesoin" anchor={c} onClose={() => setShowPositionner(false)} showNotification={showNotification} onDone={() => { onRefresh?.(); loadTimeline(); loadPositions(); }} />}

      {/* Modale : utiliser CE document pour l'un des besoins du candidat */}
      {useForBesoin && (
        <div className="ats-modal-overlay" onClick={() => !cvBesoinBusy && setUseForBesoin(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95, padding: '16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '460px', padding: '20px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)', marginBottom: '4px' }}>Utiliser « {dispName(useForBesoin, genLabel(useForBesoin))} »</div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginBottom: '14px' }}>Pour quel besoin ce CV doit-il servir ? Le matching AO lira ce document-là.</div>
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(positions || []).map((p, i) => {
                const bid = p.besoin_id ?? p.id ?? p.besoinId;
                if (bid == null) return null;
                return (
                  <button key={i} disabled={cvBesoinBusy} onClick={() => setBesoinCv(bid, useForBesoin.id)}
                    style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 12px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', cursor: cvBesoinBusy ? 'default' : 'pointer', opacity: cvBesoinBusy ? 0.6 : 1 }}>
                    <BI icon="briefcase" style={{ color: 'var(--brand)', flex: 'none' }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.titre || p.besoin_titre || `Besoin ${bid}`}</span>
                  </button>
                );
              })}
            </div>
            <button className="ats-btn ats-btn-ghost" disabled={cvBesoinBusy} onClick={() => setUseForBesoin(null)} style={{ marginLeft: 'auto', marginTop: '16px', padding: '9px 13px', borderRadius: '9px', fontSize: '12.5px', fontWeight: 600 }}>Annuler</button>
          </div>
        </div>
      )}

      {/* Modale : définir le CV spécifique d'un besoin */}
      {cvPickerBesoin && (
        <div className="ats-modal-overlay" onClick={() => !cvBesoinBusy && setCvPickerBesoin(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95, padding: '16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '460px', padding: '20px', maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)', marginBottom: '4px' }}>CV pour « {cvPickerBesoin.titre} »</div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginBottom: '14px' }}>Choisis le document du candidat à utiliser pour ce besoin (le matching AO lira ce CV-là).</div>
            <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {(() => {
                // On choisit un CV : la CNI, le permis ou un NDA n'ont rien à
                // faire dans cette liste. Reconnaissance par le drapeau serveur,
                // la provenance, ou le mot « CV » isolé dans le nom.
                const estCv = (d) => d.est_cv === true || d.source === 'cv_prep' || isOriginalCv(d)
                  || /(^|[^a-z])cv([^a-z]|$)/i.test(`${docName(d)} ${d.label || ''}`);
                const tous = (docs || []).filter(d => d.id != null);
                const liste = cvPickerTous ? tous : tous.filter(estCv);
                return (<>
              {tous.length === 0 && (
                <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun document sur la fiche. Ajoute un CV d'abord.</div>
              )}
              {tous.length > 0 && liste.length === 0 && (
                <div style={{ fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.5 }}>
                  Aucun document de la fiche n'est identifié comme CV.{' '}
                  <button onClick={() => setCvPickerTous(true)} style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--accent)', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}>
                    Afficher tous les documents
                  </button>
                </div>
              )}
              {liste.map((d, i) => (
                <button key={i} disabled={cvBesoinBusy} onClick={() => setBesoinCv(cvPickerBesoin.besoinId, d.id)} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 12px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', cursor: cvBesoinBusy ? 'default' : 'pointer', opacity: cvBesoinBusy ? 0.6 : 1 }}>
                  <BI icon="file-earmark-text" style={{ color: 'var(--brand)', flex: 'none' }} />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{docName(d)}</span>
                    <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                      {isGenerated(d) ? genLabel(d) : isOriginalCv(d) ? 'CV original' : 'Document'}{docDate(d) ? ` · ${fmtDateHeure(docDate(d))}` : ''}
                    </span>
                  </span>
                </button>
              ))}
              {!cvPickerTous && liste.length > 0 && liste.length < tous.length && (
                <button onClick={() => setCvPickerTous(true)}
                  style={{ border: 'none', background: 'none', padding: '2px 0', font: 'inherit', fontSize: '11.5px', color: 'var(--text-3)', cursor: 'pointer', textDecoration: 'underline', textAlign: 'left' }}>
                  {tous.length - liste.length} autre{tous.length - liste.length > 1 ? 's' : ''} document{tous.length - liste.length > 1 ? 's' : ''} non affiché{tous.length - liste.length > 1 ? 's' : ''} (pièces d'identité, attestations…) — tout afficher
                </button>
              )}
                </>);
              })()}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
              {cvPickerBesoin.hasCv && <button className="ats-btn ats-btn-ghost" disabled={cvBesoinBusy} onClick={() => removeBesoinCv(cvPickerBesoin.besoinId)} style={{ padding: '9px 13px', borderRadius: '9px', fontSize: '12.5px', fontWeight: 600 }}>Revenir au CV de la fiche</button>}
              <button className="ats-btn ats-btn-ghost" disabled={cvBesoinBusy} onClick={() => setCvPickerBesoin(null)} style={{ marginLeft: 'auto', padding: '9px 13px', borderRadius: '9px', fontSize: '12.5px', fontWeight: 600 }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Sélection du CV source (plusieurs CV) */}
      {cvPicker && (
        <div className="ats-modal-overlay" onClick={() => setCvPicker(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95, padding: '16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '440px', padding: '20px' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)', marginBottom: '4px' }}>Quel CV utiliser ?</div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginBottom: '14px' }}>Plusieurs CV originaux sont disponibles — choisis la source du format entreprise.</div>
            {cvDocs.map((d, i) => (
              <button key={i} onClick={() => { lancerFormatEntreprise(d.url); setCvPicker(false); }} style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', padding: '11px 12px', marginBottom: '8px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', cursor: 'pointer' }}>
                <BI icon="file-earmark-pdf" style={{ color: 'var(--brand)', flex: 'none' }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-strong)' }}>{d.name}</span>
                  {d.date && <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>{fmtDate(d.date)}</span>}
                </span>
                <BI icon="chevron-right" style={{ color: 'var(--text-faint)' }} />
              </button>
            ))}
            <button className="ats-btn ats-btn-ghost" onClick={() => setCvPicker(false)} style={{ marginTop: '4px', padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600 }}>Annuler</button>
          </div>
        </div>
      )}


      {/* Viewer de document canonique */}
      {viewerDoc && (
        <DocumentViewer candidateId={c.id} docId={viewerDoc.docId} previewBase={viewerDoc.previewBase} label={viewerDoc.label}
          cv={estCvDocument(viewerDoc)}
          besoinId={viewerDoc.besoinId} candidatNom={fullName(c)} showNotification={showNotification} onClose={() => setViewerDoc(null)} />
      )}

      {/* Renommer un document (modale in-app) */}
      {renameTarget && (
        <div className="ats-modal-overlay" onClick={() => !renaming && setRenameTarget(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 96, padding: '16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '420px', padding: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '4px' }}>
              <BI icon="pencil-square" style={{ color: 'var(--text-2)' }} />
              <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>Renommer le document</span>
            </div>
            <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginBottom: '14px' }}>Saisis le nouveau nom du document.</div>
            <input autoFocus value={renameValue} onChange={e => setRenameValue(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') doRename(); if (e.key === 'Escape' && !renaming) setRenameTarget(null); }}
              placeholder="Nom du document…"
              style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--input-border)', borderRadius: '9px', padding: '10px 12px', fontSize: '13.5px', fontFamily: 'inherit', color: 'var(--text)', background: 'var(--surface)', outline: 'none', marginBottom: '16px' }} />
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
              <button className="ats-btn ats-btn-ghost" onClick={() => setRenameTarget(null)} disabled={renaming}
                style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600, opacity: renaming ? 0.6 : 1 }}>Annuler</button>
              <button className="ats-btn ats-btn-primary" onClick={doRename} disabled={renaming || !renameValue.trim()}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600, opacity: (renaming || !renameValue.trim()) ? 0.6 : 1 }}>
                <BI icon={renaming ? 'arrow-repeat' : 'check-lg'} /> {renaming ? 'Renommage…' : 'Renommer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Style pill statut réutilisé
function statusPill(statut, theme) {
  const col = statusColors(statut || 'Nouveau', theme);
  return { display: 'inline-flex', alignItems: 'center', padding: '4px 11px', borderRadius: '7px', fontSize: '12px', fontWeight: 600, background: col.bg, color: col.fg };
}

function ShortlistChoiceModal({ candidat, onClose, onChooseBesoin, onChooseVivier, saving }) {
  const alreadyInVivier = candidat?.statut === 'Vivier';
  return (
    <div className='ats-modal-overlay' onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 96, padding: '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '460px', boxShadow: '0 24px 60px rgba(0,0,0,.35)', padding: '22px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div><div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-strong)' }}>Ajouter à la shortlist</div><div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginTop: '4px' }}>{fullName(candidat)} · Choisis la destination du candidat.</div></div>
          <button type='button' className='ats-btn ats-btn-ghost' onClick={onClose} aria-label='Fermer' style={{ width: '32px', height: '32px', borderRadius: '8px', display: 'grid', placeItems: 'center', flex: 'none' }}><BI icon='x-lg' /></button>
        </div>
        <div style={{ display: 'grid', gap: '9px', marginTop: '18px' }}>
          <button type='button' className='ats-shortlist-choice' onClick={onChooseBesoin} style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '13px 14px', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--surface)', color: 'var(--text)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' }}>
            <span style={{ width: '34px', height: '34px', borderRadius: '8px', display: 'grid', placeItems: 'center', background: 'var(--accent-soft-bg)', color: 'var(--brand)', flex: 'none' }}><BI icon='briefcase-fill' /></span>
            <span><strong style={{ display: 'block', fontSize: '13.5px', color: 'var(--text-strong)' }}>Ajouter à un besoin</strong><small style={{ display: 'block', marginTop: '3px', fontSize: '11.5px', color: 'var(--text-2)' }}>Choisir le besoin et le positionner directement.</small></span>
          </button>
          <button type='button' className='ats-shortlist-choice' onClick={onChooseVivier} disabled={saving || alreadyInVivier} style={{ display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '13px 14px', border: '1px solid var(--border)', borderRadius: '10px', background: 'var(--surface)', color: 'var(--text)', cursor: saving || alreadyInVivier ? 'not-allowed' : 'pointer', textAlign: 'left', fontFamily: 'inherit', opacity: saving || alreadyInVivier ? 0.6 : 1 }}>
            <span style={{ width: '34px', height: '34px', borderRadius: '8px', display: 'grid', placeItems: 'center', background: 'var(--accent-soft-bg)', color: 'var(--brand)', flex: 'none' }}><BI icon='people-fill' /></span>
            <span><strong style={{ display: 'block', fontSize: '13.5px', color: 'var(--text-strong)' }}>{saving ? 'Ajout au vivier…' : alreadyInVivier ? 'Déjà dans le vivier' : 'Ajouter au vivier'}</strong><small style={{ display: 'block', marginTop: '3px', fontSize: '11.5px', color: 'var(--text-2)' }}>Classer le candidat dans le vivier RH.</small></span>
          </button>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '18px' }}><button type='button' className='ats-btn ats-btn-ghost' onClick={onClose} style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600 }}>Annuler</button></div>
      </div>
    </div>
  );
}

// Modale de génération de contrat (UI ; génération PDF réelle = brief backend)
function PositionnerModal({ candidat, theme, onClose, showNotification, onDone }) {
  const [besoins, setBesoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);
  const authH = { Authorization: `Bearer ${APIService.getToken()}` };

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${BASE}/besoins?limit=200`, { headers: authH });
        const j = await r.json();
        setBesoins(j.data || j.besoins || (Array.isArray(j) ? j : []));
      } catch (e) { showNotification?.('Erreur chargement besoins : ' + e.message, 'error'); }
      finally { setLoading(false); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dejaPositionne = new Set((candidat.positionnements || []).map(p => p.besoin_id || p.id));
  const filtered = besoins.filter(b => {
    const t = `${b.titre || ''} ${b.client || ''}`.toLowerCase();
    return t.includes(q.toLowerCase());
  });

  const submit = async () => {
    if (!picked) return;
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/candidates/${candidat.id}/positionnements`, {
        method: 'POST', headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ besoinId: picked.id, besoin_id: picked.id }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      showNotification?.(`Positionné sur « ${picked.titre} »`, 'success');
      onDone?.();
    } catch (e) { showNotification?.('Erreur positionnement : ' + e.message, 'error'); }
    finally { setSaving(false); }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 95, padding: '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '520px', maxHeight: '82vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--hair)', flexShrink: 0 }}>
          <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-strong)' }}>Positionner {fullName(candidat)}</div>
          <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginTop: '3px' }}>Choisis le besoin sur lequel positionner ce candidat.</div>
          <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher un besoin / client…"
            style={{ width: '100%', marginTop: '12px', padding: '9px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text)', fontSize: '13.5px', fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }} />
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-faint)', fontSize: '13px' }}><BI icon="arrow-repeat" /> Chargement…</div>
          ) : filtered.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-faint)', fontSize: '13px' }}>Aucun besoin trouvé.</div>
          ) : filtered.map(b => {
            const sel = picked?.id === b.id;
            const deja = dejaPositionne.has(b.id);
            return (
              <button key={b.id} onClick={() => !deja && setPicked(b)} disabled={deja}
                style={{ width: '100%', textAlign: 'left', display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', marginBottom: '6px', borderRadius: '9px', cursor: deja ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: deja ? 0.5 : 1, border: sel ? '1px solid var(--text-strong)' : '1px solid var(--hair)', background: sel ? 'var(--accent-soft-bg)' : 'transparent', color: 'var(--text)' }}>
                <BI icon={sel ? 'check-circle-fill' : 'circle'} style={{ color: sel ? 'var(--brand)' : 'var(--text-faint)', flex: 'none' }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ display: 'block', fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{b.titre}</span>
                  <span style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>{b.client || ''}{deja ? ' · déjà positionné' : ''}</span>
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--hair)', display: 'flex', justifyContent: 'flex-end', gap: '8px', flexShrink: 0 }}>
          <button className="ats-btn ats-btn-ghost" onClick={onClose} style={{ padding: '9px 15px', borderRadius: '8px', fontSize: '13px', fontWeight: 600 }}>Annuler</button>
          <button className="ats-btn ats-btn-primary" onClick={submit} disabled={!picked || saving} style={{ padding: '9px 15px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, opacity: (!picked || saving) ? 0.5 : 1, cursor: (!picked || saving) ? 'not-allowed' : 'pointer' }}><BI icon={saving ? 'arrow-repeat' : 'plus-lg'} /> {saving ? 'Ajout…' : 'Positionner'}</button>
        </div>
      </div>
    </div>
  );
}

function ContratModal({ candidat, onClose, showNotification }) {
  const [type, setType] = useState(CONTRAT_TYPES[0]);
  const [start, setStart] = useState('');
  const [salaire, setSalaire] = useState(candidat.salaire_souhaite || candidat.salaire_actuel || candidat.salaire || '');

  // Données obligatoires pour un contrat
  const missingNow = [];
  if (!start) missingNow.push('Date de début');
  if (!`${salaire ?? ''}`.trim()) missingNow.push('Salaire');
  const ficheReq = [
    { label: 'Email', val: candidat.email },
    { label: 'Nationalité', val: candidat.nationalite },
    { label: 'Localisation', val: candidat.ville },
  ];
  const missingFiche = ficheReq.filter(r => !`${r.val ?? ''}`.trim()).map(r => r.label);
  const allMissing = [...missingNow, ...missingFiche];

  const generate = () => {
    if (allMissing.length) { showNotification?.('Données manquantes : ' + allMissing.join(', '), 'error'); return; }
    showNotification?.(`Contrat ${type} généré pour ${fullName(candidat)}`, 'success');
    onClose();
  };
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 90, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <div className="ats-overlay" onClick={onClose} />
      <div style={{ position: 'relative', width: '480px', maxWidth: '100%', background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', boxShadow: '0 24px 60px rgba(0,0,0,.35)', padding: '22px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: 'var(--text-strong)' }}>Générer le contrat</h2>
          <button className="ats-btn ats-btn-ghost" onClick={onClose} style={{ width: '32px', height: '32px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><BI icon="x-lg" /></button>
        </div>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '7px' }}>Type de contrat</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
          {CONTRAT_TYPES.map(t => { const sel = type === t; return <button key={t} onClick={() => setType(t)} style={{ padding: '7px 14px', borderRadius: '8px', fontSize: '12.5px', fontFamily: 'inherit', cursor: 'pointer', border: sel ? '1px solid var(--text-strong)' : '1px solid var(--input-border)', background: sel ? 'var(--ghost-bg-hover)' : 'transparent', color: 'var(--text)' }}>{t}</button>; })}
        </div>
        <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '7px' }}>Date de début *</div>
        <input type="date" className="ats-edit-input" value={start} onChange={e => setStart(e.target.value)} style={{ marginBottom: '16px' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '7px' }}>
          <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', color: 'var(--text-3)' }}>Salaire *</span>
          {!`${salaire ?? ''}`.trim() && <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--danger-fg)', background: 'var(--danger-bg)', border: '1px solid var(--danger-bd)', padding: '1px 6px', borderRadius: '5px' }}>À renseigner</span>}
        </div>
        <input className="ats-edit-input" value={salaire} onChange={e => setSalaire(e.target.value)} placeholder="ex : 120K CHF/an" style={{ marginBottom: '16px' }} />

        {allMissing.length > 0 && (
          <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-bd)', borderRadius: '9px', padding: '10px 12px', marginBottom: '16px', fontSize: '12.5px', color: 'var(--danger-fg)' }}>
            <div style={{ fontWeight: 600, marginBottom: '3px' }}><BI icon="exclamation-triangle" /> Impossible de générer — données obligatoires manquantes</div>
            {missingNow.length > 0 && <div>Ici : {missingNow.join(', ')}</div>}
            {missingFiche.length > 0 && <div>À compléter dans la fiche : {missingFiche.join(', ')}</div>}
          </div>
        )}

        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="ats-btn ats-btn-primary" onClick={generate} disabled={allMissing.length > 0}
            style={{ flex: 1, padding: '11px', borderRadius: '9px', fontSize: '13.5px', fontWeight: 600, opacity: allMissing.length > 0 ? 0.5 : 1, cursor: allMissing.length > 0 ? 'not-allowed' : 'pointer' }}>Générer le PDF</button>
          <button className="ats-btn ats-btn-ghost" onClick={onClose} style={{ padding: '11px 18px', borderRadius: '9px', fontSize: '13.5px', fontWeight: 600 }}>Annuler</button>
        </div>
      </div>
    </div>
  );
}

// Écran « Ajouter un candidat » : les entrées côte à côte — le CV qu'on a
// sous la main, le profil LinkedIn (URL directe, repli sur l'export PDF si
// LinkedIn bloque), ou la saisie À LA MAIN (09.10 : « une création simple de
// candidat sans import » — le candidat rencontré au téléphone, sans CV).
// Le bouton ouvrait un menu dont l'unique item était LinkedIn : un menu à un
// seul choix est un clic de plus pour rien, et l'import de CV n'était
// accessible nulle part depuis la base de candidats.
const FICHE_VIDE = { prenom: '', nom: '', email: '', telephone: '', titre: '', ville: '' };
function AjouterCandidatModal({ onClose, onDone, showNotification }) {
  const [entree, setEntree] = useState('cv');  // 'cv' | 'linkedin' | 'main'
  const [fiche, setFiche] = useState(FICHE_VIDE);
  const [refus, setRefus] = useState(null);       // { champ, message } — 400 du serveur, sous le champ
  const [homonyme, setHomonyme] = useState(null); // { message, existant } — 409 : même nom, autre adresse
  const [url, setUrl] = useState('');
  const [importing, setImporting] = useState(false);
  const [authwall, setAuthwall] = useState(false);
  const [survol, setSurvol] = useState(false);
  const fileRef = React.useRef(null);
  const cvRef = React.useRef(null);
  const authH = { Authorization: `Bearer ${APIService.getToken()}` };

  const finish = (data, quoi = 'LinkedIn') => {
    // id canonique pour la navigation (jamais un id legacy)
    const id = data?.canonical_candidate_id ?? data?.candidate?.canonical_candidate_id ?? data?.candidate?.id ?? data?.candidate_id ?? data?.id;
    showNotification?.(data?.created === false
      ? `Candidat déjà connu — fiche mise à jour depuis ${quoi}`
      : `Candidat importé depuis ${quoi}`, 'success');
    onDone?.(id);
  };

  // Import d'un CV : le serveur lit le document, dédoublonne (email puis
  // homonyme) et range le CV en pièce principale. Un CV illisible est REFUSÉ
  // avec le message du serveur — jamais une fiche nommée d'après le fichier.
  const importCv = async (file) => {
    if (!file) return;
    if (!/\.(pdf|docx?)$/i.test(file.name)) {
      showNotification?.(`${file.name} : format non pris en charge. CV acceptés en PDF, DOC ou DOCX.`, 'error');
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch(`${BASE}/candidates/import-cv`, { method: 'POST', headers: authH, body: fd });
      const data = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 501) { showNotification?.('Import de CV : arrive côté serveur.', 'error'); return; }
      if (!r.ok || data.success === false) throw new Error(data.error || `HTTP ${r.status}`);
      finish(data, 'le CV');
    } catch (e) { showNotification?.('Erreur import CV : ' + e.message, 'error'); }
    finally { setImporting(false); }
  };

  const importUrl = async () => {
    const linkedin_url = url.trim();
    if (!linkedin_url) return;
    setImporting(true);
    try {
      const r = await fetch(`${BASE}/candidates/import-linkedin`, { method: 'POST', headers: { ...authH, 'Content-Type': 'application/json' }, body: JSON.stringify({ linkedin_url }) });
      const data = await r.json().catch(() => ({}));
      if (r.status === 502 && data.authwall) { setAuthwall(true); showNotification?.('LinkedIn bloque la lecture directe — dépose le PDF du profil.', 'error'); return; }
      if (!r.ok || data.success === false) throw new Error(data.error || `HTTP ${r.status}`);
      finish(data);
    } catch (e) { showNotification?.('Erreur import LinkedIn : ' + e.message, 'error'); }
    finally { setImporting(false); }
  };

  const importPdf = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      if (url.trim()) fd.append('linkedin_url', url.trim());
      const r = await fetch(`${BASE}/candidates/import-linkedin`, { method: 'POST', headers: authH, body: fd });
      const data = await r.json().catch(() => ({}));
      if (!r.ok || data.success === false) throw new Error(data.error || `HTTP ${r.status}`);
      finish(data);
    } catch (e) { showNotification?.('Erreur import PDF : ' + e.message, 'error'); }
    finally { setImporting(false); }
  };

  // Saisie À LA MAIN. Le serveur dédoublonne (email, puis homonyme) : une
  // personne déjà connue est OUVERTE, jamais recréée ; un homonyme d'une autre
  // adresse est signalé et c'est la personne qui tranche.
  const champ = (k) => ({
    value: fiche[k],
    onChange: e => { const v = e.target.value; setFiche(f => ({ ...f, [k]: v })); if (refus?.champ === k) setRefus(null); setHomonyme(null); },
    onKeyDown: e => { if (e.key === 'Enter') creerALaMain(); },
    'aria-invalid': refus?.champ === k || undefined,
    disabled: importing,
  });
  const creerALaMain = async (nouvellePersonne = false) => {
    if (!fiche.nom.trim() || importing) return;
    setImporting(true); setRefus(null);
    try {
      const r = await fetch(`${BASE}/candidates`, { method: 'POST', headers: { ...authH, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...fiche, ...(nouvellePersonne ? { nouvelle_personne: true } : {}) }) });
      const data = await r.json().catch(() => ({}));
      if (r.status === 404 || r.status === 501) { showNotification?.('Création à la main : arrive côté serveur.', 'error'); return; }
      if (r.status === 409 && data.code === 'homonyme') { setHomonyme({ message: data.error, existant: data.existant }); return; }
      if (r.status === 400 && data.champ) { setRefus({ champ: data.champ, message: data.error }); return; }
      if (!r.ok || data.success === false) throw new Error(data.error || `HTTP ${r.status}`);
      const qui = [data.candidate?.prenom, data.candidate?.nom].filter(Boolean).join(' ');
      showNotification?.(data.created === false
        ? `${qui} existait déjà (${data.raison === 'email' ? 'même email' : 'même nom'}) — fiche ouverte, aucun doublon`
        : `Fiche de ${qui} créée`, 'success');
      onDone?.(data.candidate?.id);
    } catch (e) { showNotification?.('Erreur création : ' + e.message, 'error'); }
    finally { setImporting(false); }
  };

  // Onglet d'entrée : même grammaire que les filtres de la liste (pastille
  // pleine pour l'actif, contour discret sinon) — pas de nouveau composant.
  const ongletStyle = (k) => k === entree
    ? { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '7px 13px', borderRadius: '8px', border: '1px solid transparent', background: 'var(--accent)', color: 'var(--accent-fg)', fontSize: '12.5px', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' }
    : { display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '7px 13px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'transparent', color: 'var(--text-2)', fontSize: '12.5px', fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer' };

  return (
    <div className="ats-modal-overlay" onClick={() => !importing && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 96, padding: '16px' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: 'var(--panel)', border: '1px solid var(--border)', borderRadius: '14px', width: '100%', maxWidth: '460px', padding: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: '4px' }}>
          <BI icon="person-plus" style={{ fontSize: '18px', color: 'var(--text-strong)' }} />
          <span style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>Ajouter un candidat</span>
        </div>
        <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginBottom: '14px' }}>Depuis son CV, son profil LinkedIn, ou à la main.</div>

        <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
          <button onClick={() => setEntree('cv')} disabled={importing} style={ongletStyle('cv')}>
            <BI icon="file-earmark-text" /> Depuis un CV
          </button>
          <button onClick={() => setEntree('linkedin')} disabled={importing} style={ongletStyle('linkedin')}>
            <BI icon="linkedin" style={{ color: entree === 'linkedin' ? 'inherit' : '#0a66c2' }} /> Depuis LinkedIn
          </button>
          <button onClick={() => setEntree('main')} disabled={importing} style={ongletStyle('main')}>
            <BI icon="input-cursor-text" /> À la main
          </button>
        </div>

        {entree === 'main' ? (<>
          <div className="aj-main">
            <label className="aj-main-champ"><span>Prénom</span><input autoFocus className="erp-field" autoComplete="off" {...champ('prenom')} /></label>
            <label className="aj-main-champ"><span>Nom <em>obligatoire</em></span><input className="erp-field" autoComplete="off" {...champ('nom')} /></label>
            <label className="aj-main-champ"><span>Email</span><input className="erp-field" type="email" autoComplete="off" {...champ('email')} /></label>
            <label className="aj-main-champ"><span>Téléphone</span><input className="erp-field" type="tel" autoComplete="off" {...champ('telephone')} /></label>
            <label className="aj-main-champ"><span>Titre / poste</span><input className="erp-field" autoComplete="off" placeholder="ex. Développeur Java" {...champ('titre')} /></label>
            <label className="aj-main-champ"><span>Ville</span><input className="erp-field" autoComplete="off" {...champ('ville')} /></label>
          </div>
          {refus && <div className="aj-main-refus" role="alert"><BI icon="exclamation-circle" /> {refus.message}</div>}
          {homonyme ? (
            <div className="aj-main-homonyme" role="alert">
              <div><BI icon="people" /> {homonyme.message}</div>
              <div className="aj-main-homonyme-actions">
                {homonyme.existant?.id && (
                  <button className="erp-btn erp-btn--secondary" onClick={() => onDone?.(homonyme.existant.id)} disabled={importing}>
                    <BI icon="person-vcard" /> Ouvrir sa fiche
                  </button>
                )}
                <button className="erp-btn erp-btn--secondary" onClick={() => creerALaMain(true)} disabled={importing}>
                  <BI icon="person-plus" /> C'est une autre personne — créer
                </button>
              </div>
            </div>
          ) : (
            <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '10px', lineHeight: 1.5 }}>
              Le CV se dépose plus tard sur la fiche. Une personne déjà connue (même email, ou même nom) est ouverte, pas dupliquée.
            </div>
          )}
        </>) : entree === 'cv' ? (<>
          <input ref={cvRef} type="file" accept=".pdf,.doc,.docx" style={{ display: 'none' }}
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importCv(f); }} />
          <div
            onClick={() => !importing && cvRef.current?.click()}
            onDragOver={e => { e.preventDefault(); if (!importing) setSurvol(true); }}
            onDragLeave={e => { e.preventDefault(); setSurvol(false); }}
            onDrop={e => { e.preventDefault(); setSurvol(false); if (!importing) importCv(e.dataTransfer?.files?.[0]); }}
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '7px', padding: '30px 16px', borderRadius: '10px',
              border: `1px ${survol ? 'solid' : 'dashed'} ${survol ? 'var(--text-strong)' : 'var(--input-border)'}`,
              background: survol ? 'var(--accent-soft-bg)' : 'var(--ghost-bg)', cursor: importing ? 'wait' : 'pointer', transition: 'all .12s ease' }}>
            <BI icon={importing ? 'arrow-repeat' : 'cloud-arrow-up'} style={{ fontSize: '26px', color: 'var(--text-faint)' }} />
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-2)' }}>
              {importing ? 'Lecture du CV…' : 'Glissez-déposez le CV ici'}
            </span>
            <span style={{ fontSize: '12px', color: 'var(--text-faint)' }}>ou cliquez pour le choisir · PDF, DOC, DOCX</span>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginTop: '10px', lineHeight: 1.5 }}>
            Le CV est lu pour remplir la fiche (identité, titre, compétences) et rangé en pièce principale. Un candidat déjà connu est mis à jour, jamais dupliqué.
          </div>
        </>) : (<>
        <div style={{ fontSize: '12.5px', color: 'var(--text-2)', marginBottom: '10px' }}>Colle l'URL d'un profil LinkedIn (ex. linkedin.com/in/…).</div>
        <input autoFocus value={url} onChange={e => setUrl(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !authwall) importUrl(); }}
          placeholder="https://www.linkedin.com/in/…"
          style={{ width: '100%', boxSizing: 'border-box', border: '1px solid var(--input-border)', borderRadius: '9px', padding: '10px 12px', fontSize: '13.5px', fontFamily: 'inherit', color: 'var(--text)', background: 'var(--surface)', outline: 'none' }} />

        {authwall && (
          <div style={{ display: 'flex', gap: '8px', marginTop: '14px', background: 'rgba(217,119,6,0.12)', border: '1px solid rgba(217,119,6,0.25)', borderRadius: '10px', padding: '10px 12px', fontSize: '12.5px', color: '#b45309' }}>
            <BI icon="exclamation-triangle" style={{ flex: 'none', marginTop: '1px' }} />
            <span>LinkedIn bloque la lecture directe — dépose l'export PDF du profil (<b>Plus → Enregistrer au format PDF</b>).</span>
          </div>
        )}

        {/* Dépôt de l'export PDF du profil — toujours disponible (marche aussi sans passer par l'URL) */}
        <input ref={fileRef} type="file" accept=".pdf" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importPdf(f); }} />
        <button onClick={() => fileRef.current?.click()} disabled={importing}
          style={{ marginTop: '12px', width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '14px', borderRadius: '10px', border: '1px dashed var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}>
          <BI icon="cloud-arrow-up" style={{ fontSize: '17px' }} /> {importing ? 'Import…' : (authwall ? 'Déposer le PDF du profil' : 'ou déposer l\'export PDF du profil')}
        </button>
        </>)}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '16px' }}>
          <button className="ats-btn ats-btn-ghost" onClick={onClose} disabled={importing} style={{ padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600 }}>Annuler</button>
          {entree === 'main' && (
            <button className="erp-btn erp-btn--primary" onClick={() => creerALaMain()} disabled={importing || !fiche.nom.trim()}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', opacity: (importing || !fiche.nom.trim()) ? 0.6 : 1 }}>
              <BI icon={importing ? 'arrow-repeat' : 'check-lg'} /> {importing ? 'Création…' : 'Créer la fiche'}
            </button>
          )}
          {entree === 'linkedin' && !authwall && (
            <button className="ats-btn ats-btn-primary" onClick={importUrl} disabled={importing || !url.trim()} style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '8px 14px', borderRadius: '8px', fontSize: '12.5px', fontWeight: 600, opacity: (importing || !url.trim()) ? 0.6 : 1 }}>
              <BI icon={importing ? 'arrow-repeat' : 'download'} /> {importing ? 'Import…' : 'Importer'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function VueCandidats({ showNotification, theme: themeProp, onToggleTheme: onToggleThemeProp, ficheRouteId, onOpenFiche, onCloseFiche, onOpenBesoin }) {
  const [list, setList] = useState([]);
  const [ambigu, setAmbigu] = useState(null);   // saisie refusée : deux lectures possibles
  const [villesOpt, setVillesOpt] = useState([]);
  const [statutsOpt, setStatutsOpt] = useState([]); // statuts distincts présents en base (filtres.statuts)
  const [loading, setLoading] = useState(true);
  const [themeState, setThemeState] = useState(() => { try { return localStorage.getItem('ats-theme') || 'dark'; } catch { return 'dark'; } });
  const theme = themeProp || themeState;
  const controlled = themeProp != null;
  // Essai « Registre » : pilote clair uniquement, partagé entre les écrans pilotes.
  const skinV2 = true; // ERP Shell permanent
  const reduceMotion = useReducedMotion();
  const [query, setQuery] = useState('');
  const [city, setCity] = useState('Toutes les villes');
  const [status, setStatus] = useState('Tous les statuts');
  const [sort, setSort] = useState('Plus récents');
  const [selectedId, setSelectedId] = useState(null);
  // Y a-t-il encore de l'aperçu SOUS le bord ? La barre de défilement du
  // panneau est masquée : sans ce signe, un contenu coupé net au-dessus du pied
  // se lit comme un écran cassé, pas comme une liste qui continue. Le bas de
  // la zone s'estompe tant qu'il reste à voir, et redevient net au bout.
  const apercuScrollRef = React.useRef(null);
  const [apercuSuite, setApercuSuite] = useState(false);
  const mesurerApercuSuite = useCallback(() => {
    const el = apercuScrollRef.current;
    setApercuSuite(!!el && el.scrollHeight - el.scrollTop - el.clientHeight > 4);
  }, []);
  useEffect(() => {
    const el = apercuScrollRef.current;
    if (!el) { setApercuSuite(false); return undefined; }
    mesurerApercuSuite();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // La zone change de hauteur (fenêtre) ET de contenu (« + 6 compétences »,
    // un champ qu'on édite) : on observe les deux.
    const ro = new ResizeObserver(mesurerApercuSuite);
    ro.observe(el);
    Array.from(el.children).forEach(enfant => ro.observe(enfant));
    return () => ro.disconnect();
  }, [selectedId, mesurerApercuSuite]);
  const [positionnerCand, setPositionnerCand] = useState(null); // candidat à positionner depuis le panneau latéral
  const [shortlistCand, setShortlistCand] = useState(null);
  const [shortlistBusy, setShortlistBusy] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set()); // sélection multiple pour suppression
  const [deleting, setDeleting] = useState(false);
  const [deletingIds, setDeletingIds] = useState(() => new Set()); // ids en cours de suppression → lignes verrouillées
  const [showAjout, setShowAjout] = useState(false); // écran « Ajouter un candidat » (CV ou LinkedIn)
  const { confirm, confirmElement } = useConfirm();
  // Pagination serveur (GET /candidats/vue?page&limit&search&ville)
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  // LA LISTE NOIRE NE RESSORT PLUS (demande 28.09) : le serveur l'écarte de
  // la liste et de la recherche, et dit COMBIEN. L'écran le répète — une liste
  // qui cache sans le dire ferait croire que ces profils n'existent plus — et
  // propose de les afficher.
  const [listeNoireEcartes, setListeNoireEcartes] = useState(0);
  const [avecListeNoire, setAvecListeNoire] = useState(false);
  const [pages, setPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState(''); // recherche debouncée envoyée au serveur
  const [ficheFallback, setFicheFallback] = useState(null); // candidat de la fiche s'il n'est pas sur la page courante
  const [drawerDocs, setDrawerDocs] = useState(null);   // documents du candidat sélectionné (panneau latéral)
  const [drawerDetail, setDrawerDetail] = useState(null); // détail complet (positionnements) du candidat sélectionné
  const [drawerViewer, setDrawerViewer] = useState(null); // { docId, label } aperçu depuis le panneau
  // La fiche ouverte est pilotée par l'URL (/candidats/:id) → survit à un refresh
  const ficheId = ficheRouteId || null;
  const openFiche = (id) => { setSelectedId(null); if (id != null) onOpenFiche?.(id); else onCloseFiche?.(); };

  const authHeaders = { Authorization: `Bearer ${APIService.getToken()}` };

  const toggleSelect = (id) => setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const deleteCandidates = async (ids) => {
    const arr = [...ids];
    if (arr.length === 0) return;
    if (!(await confirm({
      title: arr.length === 1 ? 'Supprimer ce candidat' : `Supprimer ${arr.length} candidats`,
      message: arr.length === 1 ? 'Supprimer ce candidat de la base ?' : `Supprimer ${arr.length} candidats de la base ?`,
      detail: 'Cette action est irréversible.',
      confirmLabel: arr.length === 1 ? 'Supprimer' : `Supprimer (${arr.length})`,
    }))) return;
    setDeleting(true);
    setDeletingIds(new Set(arr)); // verrouille les lignes concernées
    let done = 0, failed = 0, bulkOk = false;
    // Suppression en lot si plusieurs (POST /candidates/bulk-delete { ids })
    if (arr.length > 1) {
      try {
        const r = await fetch(`${BASE}/candidates/bulk-delete`, { method: 'POST', headers: { ...authHeaders, 'Content-Type': 'application/json' }, body: JSON.stringify({ ids: arr }) });
        if (r.ok) {
          const j = await r.json().catch(() => ({}));
          const failedCount = Array.isArray(j.failed) ? j.failed.length : (j.failed ?? 0);
          done = j.deleted ?? (arr.length - failedCount); failed = failedCount;
          setList(prev => prev.filter(c => !arr.includes(c.id)));
          setSelectedIds(prev => { const n = new Set(prev); arr.forEach(id => n.delete(id)); return n; });
          bulkOk = true;
        }
      } catch (e) { /* repli boucle unitaire */ }
    }
    if (!bulkOk) for (const id of arr) {
      try {
        let r = await fetch(`${BASE}/candidates/${id}`, { method: 'DELETE', headers: authHeaders });
        if (r.status === 404 || r.status === 405) r = await fetch(`${BASE}/candidats/${id}`, { method: 'DELETE', headers: authHeaders });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        done++;
        // retire immédiatement de la liste + de la sélection dès que supprimé
        setList(prev => prev.filter(c => c.id !== id));
        setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      } catch (e) { failed++; }
    }
    if (selectedId && arr.includes(selectedId)) setSelectedId(null);
    showNotification?.(`${done} candidat(s) supprimé(s)${failed ? ` · ${failed} échec(s)` : ''}`, failed ? 'error' : 'success');
    setDeleting(false);
    setDeletingIds(new Set());
    load();
  };

  // Mapping des options de tri de l'UI vers les paramètres serveur sort+dir
  const SORT_MAP = { 'Plus récents': { sort: 'created_at', dir: 'desc' }, 'Nom': { sort: 'nom', dir: 'asc' } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: String(limit) });
      if (searchTerm) params.set('search', searchTerm);
      if (city !== 'Toutes les villes') params.set('ville', city);
      if (status !== 'Tous les statuts') params.set('statut', status);
      if (avecListeNoire) params.set('liste_noire', '1');
      // Recherche active : les résultats arrivent classés par pertinence
      // (identité d'abord). Imposer un tri écraserait ce classement.
      if (!searchTerm) {
        const s = SORT_MAP[sort] || SORT_MAP['Plus récents'];
        params.set('sort', s.sort); params.set('dir', s.dir);
      }
      const res = await fetch(`${BASE}/candidats/vue?${params.toString()}`, { headers: authHeaders });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Erreur');
      setList(json.data || []);
      setTotal(json.pagination?.total ?? json.total ?? (json.data || []).length);
      setPages(json.pagination?.pages ?? 1);
      setListeNoireEcartes(Number(json.liste_noire_ecartes) || 0);
      if (json.filtres?.villes) setVillesOpt(json.filtres.villes);
      if (json.filtres?.statuts) setStatutsOpt(json.filtres.statuts);
    } catch (e) {
      showNotification?.(`Erreur : ${e.message}`, 'error');
    } finally { setLoading(false); }
  }, [page, limit, searchTerm, city, status, sort, avecListeNoire]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  // Recherche debouncée → applique le terme au serveur et revient page 1
  useEffect(() => {
    const t = setTimeout(() => { setSearchTerm(query.trim()); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [query]);
  // Changement de ville / statut / tri → page 1
  useEffect(() => { setPage(1); }, [city, status, sort]);

  // Fiche par URL : si le candidat n'est pas sur la page courante, on le récupère seul.
  // ⚠️ /candidats/vue/:id (table candidate de cette liste) — PAS /candidats/:id (table Pipeline legacy).
  useEffect(() => {
    if (!ficheRouteId) { setFicheFallback(null); return; }
    if (list.find(x => String(x.id) === String(ficheRouteId))) { setFicheFallback(null); return; }
    let cancelled = false;
    fetch(`${BASE}/candidats/vue/${ficheRouteId}`, { headers: authHeaders })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(j => { if (!cancelled) setFicheFallback(j.data || j.candidat || j); })
      .catch(() => { if (!cancelled) { showNotification?.('Candidat introuvable dans la base Candidats', 'error'); onCloseFiche?.(); } });
    return () => { cancelled = true; };
  }, [ficheRouteId, list]); // eslint-disable-line react-hooks/exhaustive-deps

  // Panneau latéral (aperçu) : charge documents + détail (positionnements) du candidat sélectionné
  useEffect(() => {
    if (selectedId == null) { setDrawerDocs(null); setDrawerDetail(null); return; }
    let cancelled = false;
    setDrawerDocs(null); setDrawerDetail(null);
    fetch(`${BASE}/candidates/${selectedId}/documents`, { headers: authHeaders })
      .then(r => r.ok ? r.json() : {})
      .then(j => { if (!cancelled) { const arr = j.documents || j.data?.documents || j.candidate_documents || (Array.isArray(j.data) ? j.data : null) || (Array.isArray(j) ? j : []); setDrawerDocs(Array.isArray(arr) ? arr : []); } })
      .catch(() => { if (!cancelled) setDrawerDocs([]); });
    fetch(`${BASE}/candidats/vue/${selectedId}`, { headers: authHeaders })
      .then(r => r.ok ? r.json() : {})
      .then(j => { if (!cancelled) setDrawerDetail(j.data || j.candidat || j); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [selectedId]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleTheme = () => {
    if (controlled) { onToggleThemeProp?.(); return; }
    setThemeState(t => {
      const next = t === 'dark' ? 'light' : 'dark';
      try { localStorage.setItem('ats-theme', next); } catch {}
      return next;
    });
  };

  const changeStatut = async (id, newStatut) => {
    setList(prev => prev.map(c => c.id === id ? { ...c, statut: newStatut } : c));
    try {
      const res = await fetch(`${BASE}/candidats/vue/${id}/statut`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ statut: newStatut }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Erreur');
      return true;
    } catch (e) { showNotification?.('Statut non sauvegardé : ' + e.message, 'error'); load(); return false; }
  };

  // Édition inline d'un champ : MAJ optimiste locale (displayPatch) + PUT /candidats/:id (apiPatch),
  // rechargement en cas d'échec. Sauvegarde champ par champ pour qu'une erreur n'annule pas les autres.
  /**
   * Écriture d'un champ de la fiche.
   *
   * Un refus du serveur n'est pas un incident technique à résumer en « HTTP
   * 400 » : c'est un message écrit pour la personne qui saisit, et il est
   * affiché tel quel. Le cas le plus utile est la date ambiguë — le serveur
   * refuse de deviner et renvoie les deux écritures possibles ; on les propose
   * en deux boutons plutôt que de laisser chercher la bonne syntaxe.
   */
  const patchField = async (id, displayPatch, apiPatch) => {
    setList(prev => prev.map(c => c.id === id ? { ...c, ...displayPatch } : c));
    try {
      const res = await fetch(`${BASE}/candidats/vue/${id}`, {
        method: 'PUT', headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify(apiPatch),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) {
        if (json.ambigu === true && Array.isArray(json.lectures) && json.lectures.length) {
          // La valeur reste affichée : elle sera corrigée par le choix, pas
          // effacée sous les yeux de la personne qui vient de la taper.
          setAmbigu({ id, displayPatch, apiPatch, champ: json.champ, error: json.error, lectures: json.lectures, formats: json.formats_acceptes || [] });
          return;
        }
        throw new Error(json.error || `HTTP ${res.status}`);
      }
    } catch (e) { showNotification?.(`Modification non sauvegardée : ${e.message}`, 'error'); load(); }
  };

  // Après un changement de photo (fiche ou aperçu) : répercuter sur la ligne de
  // liste sans recharger — _photo_v casse le cache navigateur, l'URL ne
  // changeant pas d'un remplacement à l'autre.
  const majPhotoListe = (id, url, v) => {
    setList(prev => prev.map(x => x.id === id ? { ...x, photo_preview_url: url, _photo_v: v || undefined } : x));
  };

  // Choix d'une des deux écritures proposées par le serveur.
  const resoudreAmbigu = (ecriture) => {
    const a = ambigu; setAmbigu(null);
    if (!a) return;
    const champ = a.champ || Object.keys(a.apiPatch)[0];
    patchField(a.id, { ...a.displayPatch, [champ]: ecriture }, { ...a.apiPatch, [champ]: ecriture });
  };

  // Recherche, ville, statut et tri sont désormais gérés côté serveur → on affiche la page telle quelle.
  const rows = list;

  const selected = selectedId ? list.find(c => c.id === selectedId) : null;
  const selectedIndex = selected ? rows.findIndex(c => c.id === selected.id) : -1;
  const selectNeighbor = (offset) => {
    if (selectedIndex < 0 || rows.length < 2) return;
    const nextIndex = (selectedIndex + offset + rows.length) % rows.length;
    setSelectedId(rows[nextIndex].id);
  };
  const selIdx = selected ? list.indexOf(selected) : 0;
  const ficheCandidat = ficheId
    ? (list.find(c => String(c.id) === String(ficheId)) || (ficheFallback && String(ficheFallback.id) === String(ficheId) ? ficheFallback : null))
    : null;

  const ld = { color: 'var(--text-3)', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' };
  const vl = { marginTop: '4px', fontSize: '13.5px', fontWeight: 500, color: 'var(--text)' };

  return (
    <div className="ats-scope" data-theme={theme} data-skin={skinV2 ? 'v2' : undefined}>
      {confirmElement}
      {ficheCandidat ? (
        <CandidatFiche c={ficheCandidat} theme={theme} onBack={() => openFiche(null)}
          onPatch={patchField} onChangeStatut={changeStatut} showNotification={showNotification} onRefresh={load} onOpenBesoin={onOpenBesoin} onPhotoLocal={majPhotoListe} />
      ) : (
      <>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '20px', flexWrap: 'wrap' }}>
        <div>
          <h1 className="ats-h1">Candidats<span className="ats-dot">.</span></h1>
          <p style={{ margin: '6px 0 0', fontSize: '13.5px', color: 'var(--text-2)' }}>{total.toLocaleString('fr-FR')} candidats · base de talents</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/* Un seul clic : l'écran d'ajout porte les deux entrées (CV et
              LinkedIn). Le menu déroulant n'offrait qu'un choix — un clic de
              plus pour arriver au même endroit. */}
          <button {...ancre('candidat-ajouter')} onClick={() => setShowAjout(true)} className="ats-btn ats-btn-primary"
            style={{ display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: 600 }}>
            <Ic v2={skinV2} icon="person-plus" /> Ajouter un candidat
          </button>
          {/* L'essai du nouveau style : écrans pilotes uniquement, réversible d'un clic. */}
          {!controlled && !skinV2 && (
            <button onClick={toggleTheme} title="Changer de thème" className="ats-btn ats-btn-ghost"
              style={{ width: '40px', height: '40px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '17px', color: 'var(--text-2)' }}>
              <BI icon={theme === 'dark' ? 'sun' : 'moon-stars'} />
            </button>
          )}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '22px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '260px' }}>
          <Ic v2={skinV2} icon="search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-faint)', fontSize: '15px' }} />
          <input className="ats-input" value={query} onChange={e => setQuery(e.target.value)} placeholder="Rechercher un nom, titre, compétence, référence…" />
        </div>
        <ErpiaSelect className="ats-select" style={{ minWidth: '150px' }} value={city} onChange={e => setCity(e.target.value)}>
          {['Toutes les villes', ...villesOpt].map(v => <option key={v} value={v}>{v}</option>)}
        </ErpiaSelect>
        <ErpiaSelect className="ats-select" style={{ minWidth: '150px' }} value={status} onChange={e => setStatus(e.target.value)}>
          {['Tous les statuts', ...(statutsOpt.length ? statutsOpt : STEPS)].map(v => <option key={v} value={v}>{v === 'Tous les statuts' ? v : (v.charAt(0).toUpperCase() + v.slice(1))}</option>)}
        </ErpiaSelect>
        <ErpiaSelect className="ats-select" style={{ minWidth: '130px' }} value={searchTerm ? '' : sort} disabled={!!searchTerm}
          title={searchTerm ? 'Pendant une recherche, les résultats sont classés par pertinence' : undefined}
          onChange={e => setSort(e.target.value)}>
          {searchTerm && <option value="">Par pertinence</option>}
          {['Plus récents', 'Nom'].map(v => <option key={v} value={v}>{v}</option>)}
        </ErpiaSelect>
      </div>

      <div style={{ marginTop: '18px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: '12.5px', fontWeight: 500, color: 'var(--text-2)' }}>
          {total > limit
            ? `${((page - 1) * limit + 1).toLocaleString('fr-FR')}–${Math.min(page * limit, total).toLocaleString('fr-FR')} sur ${total.toLocaleString('fr-FR')}${pages > 1 ? ` · page ${page}/${pages}` : ''}`
            : `${total.toLocaleString('fr-FR')} résultat(s)`}
        </span>
        {!avecListeNoire && listeNoireEcartes > 0 && (
          <span className="cand-liste-noire-note">
            <BI icon="slash-circle" /> {listeNoireEcartes} profil{listeNoireEcartes > 1 ? 's' : ''} sur liste noire {listeNoireEcartes > 1 ? 'ne sont' : "n'est"} pas affiché{listeNoireEcartes > 1 ? 's' : ''}
            <button type="button" className="cand-liste-noire-lien" onClick={() => { setPage(1); setAvecListeNoire(true); }}>Afficher</button>
          </span>
        )}
        {avecListeNoire && (
          <span className="cand-liste-noire-note">
            <BI icon="slash-circle" /> Liste noire affichée
            <button type="button" className="cand-liste-noire-lien" onClick={() => { setPage(1); setAvecListeNoire(false); }}>Masquer</button>
          </span>
        )}
      </div>

      <div {...ancre('candidats-liste')} data-selecting={selectedIds.size > 0 ? 'true' : 'false'} style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {loading ? (
          <div style={{ padding: '48px', textAlign: 'center', color: 'var(--text-faint)', fontSize: '13.5px' }}><Ic v2={skinV2} icon="arrow-repeat" /> Chargement…</div>
        ) : rows.length === 0 ? (
          <div style={{ padding: '56px 20px', textAlign: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'var(--chip-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', color: 'var(--text-faint)', fontSize: '20px' }}><Ic v2={skinV2} icon="search" /></div>
            <p style={{ margin: 0, fontSize: '14.5px', fontWeight: 600, color: 'var(--text)' }}>Aucun candidat trouvé</p>
            <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--text-2)' }}>Essayez d'ajuster votre recherche ou vos filtres.</p>
          </div>
        ) : rows.map((c, i) => {
          const av = AV[theme][i % AV[theme].length];
          const sc = statusColors(c.statut || 'Nouveau', theme);
          const skills = splitSkills(c.competences);
          const isDel = deletingIds.has(c.id);
          return (
            <div key={c.id} className={`ats-row cand-row ${selectedId === c.id ? 'ats-row-preview-selected' : ''}`} data-sel={selectedIds.has(c.id) ? 'true' : 'false'} data-statut={c.statut || 'Nouveau'} aria-selected={selectedId === c.id ? 'true' : 'false'} aria-busy={isDel}
              onClick={isDel ? undefined : () => setSelectedId(c.id)}
              style={isDel ? { opacity: 0.5, pointerEvents: 'none', filter: 'grayscale(0.4)' } : undefined}>
              <button className="row-check" data-checked={selectedIds.has(c.id) ? 'true' : 'false'} title="Sélectionner" disabled={isDel}
                onClick={e => { e.stopPropagation(); if (!isDel) toggleSelect(c.id); }}><Ic v2={skinV2} icon="check-lg" /></button>
              <AvatarCandidat cand={c} version={c._photo_v} av={av} size="46px" radius="9px" fontSize="14px" className="cand-avatar" style={{ letterSpacing: '-0.02em' }} />
              <div className="cand-id" style={{ minWidth: '210px', maxWidth: '288px', flex: 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span className="cand-nom" style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{fullName(c)}</span>
                  {c.boond_ref && <span className="ats-mono" style={{ fontSize: '10.5px', fontWeight: 500, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '2px 6px', borderRadius: '5px', whiteSpace: 'nowrap', flex: 'none' }}>{c.boond_ref}</span>}
                  {/* Pourquoi ce profil remonte : renvoyé seulement en recherche. */}
                  {MATCH_REASON[c.match_reason] && (
                    <span title={`Ce profil correspond à votre recherche par ${MATCH_REASON[c.match_reason].long}`}
                      style={{ fontSize: '10.5px', fontWeight: 600, color: 'var(--text-3)', background: 'var(--chip-bg)', padding: '2px 7px', borderRadius: '5px', whiteSpace: 'nowrap', flex: 'none' }}>
                      {MATCH_REASON[c.match_reason].court}
                    </span>
                  )}
                </div>
                <div className="cand-titre" style={{ marginTop: '3px', fontSize: '12.5px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.titre || '—'}</div>
              </div>
              {/* Compétences en une seule ligne clampée ; masquées <960px (v2 §5) */}
              <div className="rsp-hide-md ats-skills-line" style={{ flex: 1, minWidth: 0 }}>
                {skills.map((s, k) => <span key={k} style={{ fontSize: '11.5px', fontWeight: 500, color: 'var(--chip-fg)', background: 'var(--chip-bg)', padding: '4px 9px', borderRadius: '6px', whiteSpace: 'nowrap' }}>{s}</span>)}
              </div>
              <span className="cand-statut" style={{ display: 'inline-flex', alignItems: 'center', flex: 'none', padding: '4px 11px', borderRadius: '7px', fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', background: sc.bg, color: sc.fg }}>{c.statut || 'Nouveau'}</span>
              <div className="rsp-hide-md" style={{ flex: 'none', width: '120px', display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-2)', fontSize: '12.5px' }}>
                {c.ville && <><Ic v2={skinV2} icon="geo-alt" style={{ flex: 'none', fontSize: '13px' }} /><span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.ville}</span></>}
              </div>
              {/* Manager (resp.) — masqué <1200px */}
              <div className="rsp-hide-lg" style={{ flex: 'none', width: '132px', display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-2)', fontSize: '12.5px', minWidth: 0 }} title={candManager(c) || undefined}>
                {candManager(c) && <><Ic v2={skinV2} icon="person" style={{ flex: 'none', fontSize: '13px', color: 'var(--text-3)' }} /><span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{candManager(c)}</span></>}
              </div>
              {/* Date de MAJ — masquée <1200px */}
              <div className="rsp-hide-lg ats-mono" style={{ flex: 'none', width: '68px', textAlign: 'right', color: 'var(--text-faint)', fontSize: '12px', whiteSpace: 'nowrap' }} title={candMaj(c) ? `Mise à jour le ${fmtDate(candMaj(c))}` : undefined}>
                {fmtDateShort(candMaj(c))}
              </div>
              {isDel
                ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', flex: 'none', fontSize: '12px', color: 'var(--text-3)' }}><Ic v2={skinV2} icon="arrow-repeat" style={{ animation: 'ats-spin .7s linear infinite' }} /> Suppression…</span>
                : <Ic v2={skinV2} icon="chevron-right" style={{ color: 'var(--text-faint)', flex: 'none', fontSize: '16px' }} />}
            </div>
          );
        })}
      </div>

      {/* Pageur serveur — pilule unique (SPEC_background_plage_pagination §2) :
          segments ronds dedans, page active en --accent, chevrons inertes aux extrémités,
          troncature 1 … courante ±1 … dernière. */}
      {pages > 1 && !loading && (() => {
        const go = (p) => { const np = Math.max(1, Math.min(pages, p)); if (np !== page) { setPage(np); window.scrollTo({ top: 0, behavior: 'smooth' }); } };
        const win = []; const from = Math.max(1, page - 1), to = Math.min(pages, page + 1);
        for (let p = from; p <= to; p++) win.push(p);
        return (
          <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
            <div className="ats-pager">
              <button className="ats-pager-nav" onClick={() => go(page - 1)} disabled={page <= 1} aria-label="Page précédente"><Ic v2={skinV2} icon="chevron-left" /></button>
              {from > 1 && <><button onClick={() => go(1)}>1</button>{from > 2 && <span className="ats-pager-gap">…</span>}</>}
              {win.map(p => <button key={p} onClick={() => go(p)} aria-current={p === page ? 'page' : undefined}>{p}</button>)}
              {to < pages && <>{to < pages - 1 && <span className="ats-pager-gap">…</span>}<button onClick={() => go(pages)}>{pages}</button></>}
              <button className="ats-pager-nav" onClick={() => go(page + 1)} disabled={page >= pages} aria-label="Page suivante"><Ic v2={skinV2} icon="chevron-right" /></button>
            </div>
          </div>
        );
      })()}

      {/* Barre d'actions flottante — visible dès qu'un candidat est coché.
          En essai Registre, l'entrée/sortie passe par Motion (160 ms ease-out,
          rien si l'utilisateur préfère réduire les animations) ; en v1 le
          rendu est identique à avant. */}
      <AnimatePresence>
      {selectedIds.size > 0 && (() => {
        const pageAllSelected = rows.length > 0 && rows.every(c => selectedIds.has(c.id));
        const togglePage = () => setSelectedIds(prev => { const n = new Set(prev); if (pageAllSelected) rows.forEach(c => n.delete(c.id)); else rows.forEach(c => n.add(c.id)); return n; });
        const barBtn = { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' };
        const animer = skinV2 && !reduceMotion;
        return (
          <MotionDiv key="cand-actionbar" className="cand-actionbar"
            initial={animer ? { opacity: 0, y: 10, x: '-50%' } : false}
            animate={animer ? { opacity: 1, y: 0, x: '-50%' } : undefined}
            exit={animer ? { opacity: 0, y: 10, x: '-50%' } : undefined}
            transition={{ duration: 0.16, ease: 'easeOut' }}>
            <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-strong)' }}>{selectedIds.size} sélectionné{selectedIds.size > 1 ? 's' : ''}</span>
            <button onClick={togglePage} style={barBtn}><Ic v2={skinV2} icon={pageAllSelected ? 'dash-square' : 'check2-square'} /> {pageAllSelected ? 'Désélectionner la page' : `Sélectionner la page (${rows.length})`}</button>
            <button onClick={() => deleteCandidates(selectedIds)} disabled={deleting}
              style={{ ...barBtn, border: 'none', background: 'var(--brand)', color: '#fff', opacity: deleting ? 0.6 : 1 }}>
              <Ic v2={skinV2} icon={deleting ? 'arrow-repeat' : 'trash3'} /> {deleting ? 'Suppression…' : 'Supprimer'}
            </button>
            <button onClick={() => setSelectedIds(new Set())} style={{ ...barBtn, background: 'transparent', border: 'none', color: 'var(--text-2)' }}>Effacer</button>
          </MotionDiv>
        );
      })()}
      </AnimatePresence>

      {selected && (() => {
        const av = AV[theme][selIdx % AV[theme].length];
        const selProvType = (selected.provenance_type || '').toLowerCase();
        const selSourceUrl = /^https?:\/\//i.test(selected.provenance_detail || '') ? selected.provenance_detail : null;
        const selSourceLabel = (selProvType === 'linkedin' ? 'LinkedIn' : selProvType === 'boond' ? 'Import Boond' : (selected.provenance_detail || selected.provenance_type || '—'))
          .replace(/\s*\((?:profil\s+)?linkedin[^)]*\)/i, '').trim(); // id recruiter masqué (même règle que la fiche)
        const positions = (drawerDetail?.positionnements || selected.positionnements || []);
        const openBesoin = (id) => { if (id == null) return; if (onOpenBesoin) onOpenBesoin(id); else window.location.hash = '/besoins/' + id; };
        const drawerDocName = (d) => d.label || d.name || d.original_name || d.type_piece || d.type || 'Document';
        const drawerDlHref = (d) => d.id != null ? avecCle(`${BASE}/candidates/${selected.id}/documents/${d.id}/preview?download=1`) : (d.url || d.cv_url || null);
        const contactHref = selected.email ? `mailto:${selected.email}` : selected.telephone ? `tel:${selected.telephone}` : null;
         const lastActivityAt = candMaj(selected) || selected.cv_updated_at || selected.updated_at || selected.created_at || null;
         const lastActivityLabel = lastActivityAt ? 'Profil mis à jour' : 'Profil créé';
         const lastActivityDetail = lastActivityAt ? 'Import CV et vérification des compétences · ' + fmtDate(lastActivityAt) : 'Date de dernière activité non renseignée';
        const source = selSourceUrl
          ? <a href={selSourceUrl} target="_blank" rel="noreferrer" style={{ color: 'var(--text-strong)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '5px' }}>{selProvType === 'linkedin' && <BI icon="linkedin" style={{ color: '#0a66c2' }} />}{selSourceLabel} <BI icon="box-arrow-up-right" style={{ fontSize: '11px', color: 'var(--text-3)' }} /></a>
          : selSourceLabel;
        return (
          <div className="ats-preview-overlay" style={{ position: 'fixed', inset: 0, zIndex: 80 }}>
            <div className="ats-overlay ats-preview-backdrop" onClick={() => setSelectedId(null)} />
            <div className="ats-drawer ats-preview-drawer">
              <div className="ats-preview-header" style={{ padding: '22px 24px 18px', borderBottom: '1px solid var(--border)', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                <AvatarPhotoEditable cand={selected} av={av}
                  urlServeur={selected.photo_preview_url ?? drawerDetail?.photo_preview_url}
                  size="54px" radius="10px" fontSize="17px" className="ats-preview-avatar" style={{ letterSpacing: '-0.02em' }}
                  showNotification={showNotification} onPhoto={(u, v) => majPhotoListe(selected.id, u, v)} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <h2 className="ats-h2-drawer ats-preview-name">{fullName(selected)}</h2>
                    {selected.boond_ref && <span className="ats-mono" style={{ fontSize: '10.5px', fontWeight: 500, color: 'var(--text-2)', background: 'var(--code-bg)', padding: '2px 6px', borderRadius: '5px', flex: 'none' }}>{selected.boond_ref}</span>}
                  </div>
                  <div style={{ margin: '5px 0 0' }}>
                    <EditField value={selected.titre} placeholder="+ titre / poste"
                      onSave={v => patchField(selected.id, { titre: v }, { titre: v })}
                      displayStyle={{ fontSize: '13.5px', color: 'var(--text-2)', lineHeight: 1.4 }}
                      inputStyle={{ fontSize: '13.5px' }} />
                  </div>
                </div>
                <button className="ats-btn ats-btn-ghost ats-preview-close" onClick={() => setSelectedId(null)} style={{ width: '34px', height: '34px', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none', color: 'var(--text-2)', fontSize: '15px' }}><BI icon="x-lg" /></button>
              </div>

              <div ref={apercuScrollRef} onScroll={mesurerApercuSuite} className={`ats-preview-scroll${apercuSuite ? ' ats-preview-scroll--suite' : ''}`} style={{ flex: 1, overflowY: 'auto' }}>
                <div className="ats-preview-actions" style={{ padding: '18px 24px', display: 'flex', gap: '8px', flexWrap: 'wrap', borderBottom: '1px solid var(--hair)' }}>
                  <a href={contactHref || undefined} className="ats-btn ats-btn-primary ats-preview-contact" style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 13px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, textDecoration: 'none', opacity: contactHref ? 1 : 0.5 }}><BI icon="send" /> Contacter</a>
                  <button type="button" className="ats-btn ats-btn-ghost ats-preview-shortlist" onClick={() => setShortlistCand(selected)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '9px 13px', borderRadius: '8px', fontSize: '13px', fontWeight: 600 }}><BI icon="star" /> Shortlist</button>
                  <a href={selected.telephone ? `tel:${selected.telephone}` : undefined} className="ats-btn ats-btn-ghost" style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 13px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, textDecoration: 'none', opacity: selected.telephone ? 1 : 0.5 }}><BI icon="telephone" /> Appeler</a>
                  {(() => {
                    const li = selected.linkedin_url || selected.linkedin || (selSourceUrl && /linkedin\.com/i.test(selSourceUrl) ? selSourceUrl : null);
                    return (
                      <a href={li || undefined} target={li ? '_blank' : undefined} rel="noreferrer"
                        title={li ? 'Ouvrir le profil LinkedIn' : 'Aucun profil LinkedIn renseigné'}
                        className="ats-btn ats-btn-ghost"
                        style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '9px 13px', borderRadius: '8px', fontSize: '13px', fontWeight: 600, textDecoration: 'none', opacity: li ? 1 : 0.5, cursor: li ? 'pointer' : 'not-allowed' }}>
                        <BI icon="linkedin" /> LinkedIn
                      </a>
                    );
                  })()}
                </div>

                               <div className="ats-drawer-kicker"><span>APERÇU RAPIDE</span><strong>{selectedIndex >= 0 ? selectedIndex + 1 : 1} / {rows.length}</strong></div>
               <div className="ats-drawer-status-panel">
                 <div className="ats-drawer-section-label">Statut & disponibilité</div>
                 <div className="ats-drawer-status-line"><strong>{selected.statut || 'Nouveau'}</strong><span><i className="bi bi-circle-fill" /> {selected.disponibilite || 'Disponibilité à confirmer'}</span></div>
               </div>

<div className="ats-preview-info-section" style={{ padding: '18px 24px', borderBottom: '1px solid var(--hair)' }}>
  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '12px' }}>Coordonnées</div>
  <div style={{ display: 'grid', gap: '10px' }}>
    <div className="ats-preview-contact-row" style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12.5px', color: 'var(--text-2)' }}><BI icon="geo-alt" style={{ color: 'var(--brand)', flex: 'none' }} /><EditField value={selected.ville} placeholder="Localisation à préciser" displayStyle={{ fontSize: '12.5px', color: 'var(--text-2)' }} onSave={v => patchField(selected.id, { ville: v }, { localisation: v })} /></div>
    <div className="ats-preview-contact-row" style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12.5px', color: 'var(--text-2)' }}><BI icon="envelope" style={{ color: 'var(--brand)', flex: 'none' }} /><EditField value={selected.email} placeholder="Email à renseigner" displayStyle={{ fontSize: '12.5px', color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} onSave={v => patchField(selected.id, { email: v }, { email: v })} /></div>
    <div className="ats-preview-contact-row" style={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: '12.5px', color: 'var(--text-2)' }}><BI icon="briefcase" style={{ color: 'var(--brand)', flex: 'none' }} /><span>Référent : {candManager(selected) || 'À attribuer'}</span></div>
  </div>
</div>

<div className="ats-preview-info-section" style={{ padding: '18px 24px', borderBottom: '1px solid var(--hair)' }}>
  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '11px' }}>Compétences clés</div>
  <SkillsEditor compact value={selected.competences || selected.skills} onSave={v => patchField(selected.id, { competences: v }, { competences: v })} />
</div>

<div className="ats-preview-info-section" style={{ padding: '18px 24px', borderBottom: '1px solid var(--hair)' }}>
  <div style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-3)', marginBottom: '13px' }}>Dernière activité</div>
  <div style={{ borderLeft: '1px solid var(--border)', paddingLeft: '12px' }}>
    <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)' }}>{lastActivityLabel}</div>
    <div style={{ fontSize: '11.5px', color: 'var(--text-3)', marginTop: '3px' }}>{lastActivityDetail}</div>
  </div>
</div>
              </div>
              {/* Hors de la zone qui défile (vécu 28.09) : le pied — « Ouvrir la fiche
                  complète », « Ajouter à un besoin » et les flèches — restait au bout
                  du contenu, sous le bord du panneau, et la barre de défilement est
                  masquée : on ne le voyait pas. Il est désormais toujours là. */}
              <div className="ats-preview-nav" style={{ flex: 'none', padding: '10px 24px 0', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <span className="ats-mono" style={{ fontSize: '10.5px', color: 'var(--text-3)', letterSpacing: '0.04em', textTransform: 'uppercase' }}>Aperçu rapide · {selectedIndex >= 0 ? selectedIndex + 1 : 1} / {rows.length}</span>
                <div style={{ display: 'flex', gap: '5px' }}>
                  <button type="button" data-testid="button-candidat-preview-previous" aria-label="Candidat précédent" disabled={rows.length < 2} onClick={() => selectNeighbor(-1)} className="ats-btn ats-btn-ghost" style={{ width: '34px', height: '32px', borderRadius: '8px', display: 'grid', placeItems: 'center', opacity: rows.length < 2 ? 0.45 : 1 }}><Ic v2={skinV2} icon="chevron-left" /></button>
                  <button type="button" data-testid="button-candidat-preview-next" aria-label="Candidat suivant" disabled={rows.length < 2} onClick={() => selectNeighbor(1)} className="ats-btn ats-btn-ghost" style={{ width: '34px', height: '32px', borderRadius: '8px', display: 'grid', placeItems: 'center', opacity: rows.length < 2 ? 0.45 : 1 }}><Ic v2={skinV2} icon="chevron-right" /></button>
                </div>
              </div>
              <div className="ats-preview-footer" style={{ flex: 'none', padding: '10px 24px 14px', display: 'flex', gap: '10px' }}>
                <button className="ats-btn ats-btn-ghost ats-preview-position" onClick={() => setPositionnerCand(selected)} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '11px', borderRadius: '8px', fontSize: '13.5px', fontWeight: 600 }}><BI icon="plus-lg" /> Ajouter à un besoin</button>
                <button className="ats-btn ats-btn-primary ats-preview-open-full" onClick={() => openFiche(selected.id)} style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: '7px', padding: '11px 16px', borderRadius: '8px', fontSize: '13.5px', fontWeight: 600 }}><BI icon="arrows-angle-expand" /> Ouvrir la fiche complète</button>
              </div>
          </div>
          </div>
        );
      })()}
      </>
      )}

      {/* Positionner sur un besoin depuis le panneau latéral */}
      {shortlistCand && <ShortlistChoiceModal candidat={shortlistCand} saving={shortlistBusy} onClose={() => { if (!shortlistBusy) setShortlistCand(null); }} onChooseBesoin={() => { const candidate = shortlistCand; setShortlistCand(null); setPositionnerCand(candidate); }} onChooseVivier={async () => { if (!shortlistCand) return; setShortlistBusy(true); const ok = await changeStatut(shortlistCand.id, 'Vivier'); setShortlistBusy(false); if (ok) { showNotification?.('Candidat ajouté au vivier.', 'success'); setShortlistCand(null); } }} />}
      {positionnerCand && (
        <AssignMatchingModal mode="pickBesoin" anchor={positionnerCand} showNotification={showNotification}
          onClose={() => setPositionnerCand(null)} onDone={() => { setPositionnerCand(null); load(); }} />
      )}

      {/* Ajouter un candidat : CV (dépôt/parcourir) ou LinkedIn (URL → repli PDF) */}
      {showAjout && (
        <AjouterCandidatModal showNotification={showNotification}
          onClose={() => setShowAjout(false)}
          onDone={(id) => { setShowAjout(false); load(); if (id != null) openFiche(id); }} />
      )}

      {/* Saisie refusée parce qu'elle se lit de deux façons. Le serveur donne
          les deux écritures non ambiguës : on les propose, on ne fait pas
          deviner une syntaxe. */}
      {ambigu && (
        <div onClick={() => { setAmbigu(null); load(); }} style={{ position: 'fixed', inset: 0, zIndex: 900, background: 'rgba(5,6,8,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
          <div onClick={e => e.stopPropagation()} style={{ width: 'min(440px, 96vw)', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '14px', padding: '20px 22px' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: 'var(--text-strong)' }}>Vous vouliez dire ?</div>
            <div style={{ marginTop: '6px', fontSize: '12.5px', color: 'var(--text-2)', lineHeight: 1.55 }}>{ambigu.error}</div>
            <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
              {ambigu.lectures.map(l => (
                <button key={l} onClick={() => resoudreAmbigu(l)} className="ats-btn"
                  style={{ padding: '10px 13px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)', fontFamily: "'IBM Plex Mono',monospace", fontSize: '13px', fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
                  {l}
                </button>
              ))}
            </div>
            {ambigu.formats?.length > 0 && (
              <div style={{ marginTop: '12px', fontSize: '11.5px', color: 'var(--text-3)', lineHeight: 1.5 }}>
                Écritures acceptées : {ambigu.formats.join(' · ')}
              </div>
            )}
            <div style={{ marginTop: '14px', display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={() => { setAmbigu(null); load(); }}
                style={{ padding: '8px 13px', borderRadius: '9px', border: '1px solid var(--input-border)', background: 'transparent', color: 'var(--text-2)', fontFamily: 'inherit', fontSize: '12.5px', fontWeight: 600, cursor: 'pointer' }}>
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Aperçu document depuis le panneau latéral */}
      {drawerViewer && (
        <DocumentViewer candidateId={drawerViewer.candidateId} docId={drawerViewer.docId} label={drawerViewer.label}
          candidatNom={drawerViewer.candidatNom} showNotification={showNotification} onClose={() => setDrawerViewer(null)} />
      )}
    </div>
  );
}
